'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Spin, message } from 'antd'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import studentRequest from 'utils/studentRequest'
import { getUserMediaWithSelectedDevices } from 'utils/mediaDevices'
import { mountHiddenProctorVideo, unmountProctorVideo, watchProctorTrack } from 'utils/proctorVideo'
import useProctoringCollector from 'utils/useProctoringCollector'
import useCameraGuard from 'utils/useCameraGuard'
import { shouldEnforceFullscreen } from 'utils/deviceTier'
import { pickAudioMimeType, resolveRecordedMimeType } from 'utils/audioFormat'
import { PCM_WORKLET_SOURCE } from './pcmWorklet'
import { getTtsAudio } from './ttsAudio'
import { analyzeAnswerDelivery } from './answerDelivery'
import {
  startSession, submitTurn, completeSession, fetchReport, tts, stt, uploadSessionAudio,
  deliveryTelemetry, backfillDeliveryTelemetry,
} from './aiInterviewAPI'
import { readInviteScopedJwt, markAiInterviewCompleted } from './aiInterviewInviteAPI'
import {
  startSessionRecording, attachMicToSessionRecording, attachTtsToSessionRecording,
  stopSessionRecording, teardownSessionRecording, getRecordingElapsedMs,
  snapshotSessionRecording,
} from './sessionRecorder'
import {
  enqueueRecording, drainPendingUploads, initUploadQueueResumption,
} from './sessionUploadQueue'

/* ---------------------------------------------------------------------------
 * Design-system aligned interview UI — matches dev.pluginlive.com/design/ai-interview.
 * Tokens (single source of truth): cobalt #494FDF, neutral grays, 6px radii,
 * Satoshi/Inter, 4px spacing grid.
 * ------------------------------------------------------------------------- */
const TOKEN = {
  bgPage: '#FFFFFF',
  bgSurface: '#F5F5F4',
  bgElevated: '#FFFFFF',
  bgBrand: '#0A0A0A',
  bgBrandSubtle: '#EEEFFE',
  bgDangerSubtle: '#FCEDED',
  bgInfoSubtle: '#EBF3FB',
  bgSuccessSubtle: '#E6F8EE',
  borderDefault: '#E7E5E4',
  borderStrong: '#D6D3D1',
  borderBrand: '#494FDF',
  textPrimary: '#1C1917',
  textBody: '#292524',
  textMuted: '#78716C',
  textDisabled: '#A8A29E',
  textInverse: '#FFFFFF',
  textBrand: '#494FDF',
  primary50: '#F4F5FE',
  primary200: '#C9CCFB',
  primary300: '#A8ACF6',
  primary400: '#7A7EEC',
  primary500: '#494FDF',
  primary600: '#494FDF',
  primary700: '#3A3EB3',
  primary800: '#2D3094',
  error: '#E23B4A',
  errorHover: '#C12B39',
  warning: '#E8A500',
  success: '#1FA56B',
  info: '#2563EB',
  radiusSm: '4px',
  radiusMd: '6px',
  shadowSm: '0 1px 2px 0 rgba(26,25,23,0.06), 0 1px 4px 0 rgba(26,25,23,0.04)',
  shadowMd: '0 4px 8px -2px rgba(26,25,23,0.08), 0 2px 6px -2px rgba(26,25,23,0.06)',
  shadowLg: '0 12px 24px -4px rgba(26,25,23,0.10), 0 4px 10px -4px rgba(26,25,23,0.08)',
  fontSans: "'Satoshi', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontMono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
}

const Shell = styled.div`
  position: fixed;
  inset: 0;
  background: ${TOKEN.bgPage};
  font-family: ${TOKEN.fontSans};
  color: ${TOKEN.textBody};
  z-index: 9999;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -webkit-font-smoothing: antialiased;
`
const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 40;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 22px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid ${TOKEN.borderDefault};

  @media (max-width: 640px) {
    padding: 0 14px;
    height: 54px;
  }
`
const Brand = styled.div`
  font-weight: 700;
  color: ${TOKEN.textPrimary};
  font-size: 15px;
  letter-spacing: -0.01em;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`
const RoleTag = styled.div`
  color: ${TOKEN.textMuted};
  font-size: 12.5px;
  font-weight: 500;
  margin-top: 1px;

  @media (max-width: 640px) {
    font-size: 11.5px;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
const ElapsedPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 12px;
  border-radius: ${TOKEN.radiusMd};
  background: ${TOKEN.bgSurface};
  box-shadow: inset 0 0 0 1px ${TOKEN.borderDefault};
  font-family: ${TOKEN.fontMono};
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 14px;
  color: ${TOKEN.textBrand};
  flex-shrink: 0;
`
const Body = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 48px 24px 96px;

  @media (max-width: 640px) {
    padding: 24px 14px 80px;
  }
`
const Card = styled.div`
  background: ${TOKEN.bgElevated};
  border: 1px solid ${TOKEN.borderDefault};
  border-radius: ${TOKEN.radiusMd};
  box-shadow: ${TOKEN.shadowSm};
  margin-bottom: 16px;

  @media (max-width: 640px) {
    margin-bottom: 12px;
  }
`
const Eyebrow = styled.div`
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${TOKEN.textBrand};
  font-weight: 700;
  margin-bottom: 10px;
`

const blink = keyframes`50% { opacity: 0.25; }`
const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50%     { transform: scale(1.06); }
`
const radiate = keyframes`
  0%   { transform: scale(0.55); opacity: 0.7; }
  100% { transform: scale(1.00); opacity: 0;   }
`
const ripple = keyframes`
  0%   { transform: scale(0.70); opacity: 0.6; }
  100% { transform: scale(1.05); opacity: 0;   }
`
const shimmerKf = keyframes`
  0%   { background-position: 100% 0; }
  100% { background-position:   0% 0; }
`
const qpulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.8); }
  50%     { opacity: 1;    transform: scale(1.15); }
`
const caretBlink = keyframes`
  0%, 49%   { opacity: 1; }
  50%, 100% { opacity: 0; }
`
const SubtitleCaret = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.05em;
  background: ${TOKEN.primary600};
  margin-left: 1px;
  vertical-align: -2px;
  animation: ${caretBlink} 1s steps(2, jump-none) infinite;
`

/* ---- Abstract AI avatar (orb), 3 states: idle / speaking / listening ----
 * Mirrors the design-system .avatar markup: 3 rings + a radial-gradient core,
 * with a soft inner shimmer. Never a face — visualises voice presence only.
 */
const Avatar = styled.div`
  position: relative;
  width: 128px;
  height: 128px;
  margin: 4px auto 26px;

  @media (max-width: 640px) {
    width: 104px;
    height: 104px;
    margin: 2px auto 22px;
  }
`
const AvatarRing = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px solid ${TOKEN.primary200};
  opacity: 0;
  pointer-events: none;
`
const AvatarCore = styled.div`
  position: absolute;
  inset: 30px;
  border-radius: 50%;
  background: radial-gradient(120% 120% at 35% 30%, ${TOKEN.primary400}, ${TOKEN.primary700});
  box-shadow: 0 8px 24px -8px rgba(73, 79, 223, 0.6);

  &::after {
    content: '';
    position: absolute;
    inset: 18%;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    filter: blur(4px);
  }

  /* idle: gentle breathing */
  animation: ${breathe} 3.6s ease-in-out infinite;

  ${(p) =>
    p.speaking &&
    css`
      animation: ${breathe} 1.4s ease-in-out infinite;
    `}

  ${(p) =>
    p.listening &&
    css`
      background: radial-gradient(120% 120% at 35% 30%, ${TOKEN.primary300}, ${TOKEN.primary600});
      animation: ${breathe} 2.2s ease-in-out infinite;
    `}
`
const AvatarRingsBox = styled.div`
  position: absolute;
  inset: 0;
  ${(p) =>
    p.speaking &&
    css`
      ${AvatarRing} {
        animation: ${radiate} 1.8s ease-out infinite;
      }
      ${AvatarRing}:nth-child(2) { animation-delay: 0.6s; }
      ${AvatarRing}:nth-child(3) { animation-delay: 1.2s; }
    `}
  ${(p) =>
    p.listening &&
    css`
      ${AvatarRing}:first-child {
        border-color: ${TOKEN.primary300};
        animation: ${ripple} 2.2s ease-out infinite;
        opacity: 0.6;
      }
    `}
`
const AvatarStateLabel = styled.div`
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${TOKEN.textDisabled};
`

/* Centered question text — the focal point of the live screen. */
const QuestionText = styled.div`
  font-size: 22px;
  line-height: 1.4;
  font-weight: 600;
  color: ${TOKEN.textPrimary};
  max-width: 46ch;
  margin: 0 auto;
  letter-spacing: -0.01em;

  @media (max-width: 640px) {
    font-size: 18px;
    line-height: 1.42;
  }
`

/* "Cooking another question…" loader — shimmer bars + pulsing dot. */
const QLoader = styled.div`
  margin: 26px auto 0;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`
const QlShim = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;

  i {
    height: 18px;
    border-radius: ${TOKEN.radiusSm};
    background: linear-gradient(90deg, ${TOKEN.bgSurface} 25%, #E7E5E4 37%, ${TOKEN.bgSurface} 63%);
    background-size: 400% 100%;
    animation: ${shimmerKf} 1.4s ease infinite;
    display: block;
  }
  i:nth-child(1) { width: 78%; }
  i:nth-child(2) { width: 94%; }
  i:nth-child(3) { width: 56%; }
`
const QlMsg = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  font-weight: 600;
  color: ${TOKEN.textBrand};

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${TOKEN.textBrand};
    animation: ${qpulse} 1s ease-in-out infinite;
  }
`

/* Read-only transcript panel (live captions while candidate is speaking). */
const TranscriptBox = styled.div`
  margin: 26px auto 0;
  max-width: 560px;
  text-align: left;
  background: ${TOKEN.bgSurface};
  border: 1px solid ${TOKEN.borderDefault};
  border-radius: ${TOKEN.radiusMd};
  padding: 18px 20px;
  min-height: 96px;
  position: relative;

  .tl {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${TOKEN.textDisabled};
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .tl .live {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${TOKEN.error};
  }
  .tl .live .d {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${TOKEN.error};
    animation: ${blink} 1.4s infinite;
  }
  .body {
    font-size: 15px;
    line-height: 1.6;
    color: ${TOKEN.textBody};
  }
  .body .ph { color: ${TOKEN.textDisabled}; }
`
const HintRow = styled.div`
  font-size: 13px;
  color: ${TOKEN.textDisabled};
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  justify-content: center;
  text-align: center;
`
const IvControls = styled.div`
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-height: 50px;

  .row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    flex-wrap: wrap;
  }
`

/* Buttons follow design-system .btn — 44px h, 6px radius, semibold, cobalt. */
const PrimaryBtn = styled(Button)`
  background: ${TOKEN.primary600};
  border-color: ${TOKEN.primary600};
  color: #fff;
  font-weight: 600;
  height: 44px;
  border-radius: ${TOKEN.radiusMd};
  padding: 0 24px;
  font-size: 15px;
  box-shadow: none;
  &:hover, &:focus { background: ${TOKEN.primary700}; border-color: ${TOKEN.primary700}; color: #fff; }
  &:active { background: ${TOKEN.primary800}; border-color: ${TOKEN.primary800}; }

  @media (max-width: 640px) {
    width: 100%;
    height: 48px;
  }
`
const DangerBtn = styled(Button)`
  background: ${TOKEN.error};
  border-color: ${TOKEN.error};
  color: #fff;
  font-weight: 600;
  height: 44px;
  border-radius: ${TOKEN.radiusMd};
  padding: 0 24px;
  font-size: 15px;
  &:hover, &:focus { background: ${TOKEN.errorHover}; border-color: ${TOKEN.errorHover}; color: #fff; }

  @media (max-width: 640px) {
    width: 100%;
    height: 48px;
  }
`
const GhostBtn = styled(Button)`
  background: ${TOKEN.bgElevated};
  color: ${TOKEN.textBody};
  height: 44px;
  border-radius: ${TOKEN.radiusMd};
  padding: 0 24px;
  font-weight: 600;
  font-size: 15px;
  box-shadow: inset 0 0 0 1px ${TOKEN.borderDefault};
  border: none;
  &:hover, &:focus { background: ${TOKEN.bgSurface}; color: ${TOKEN.textBody}; }

  @media (max-width: 640px) {
    height: 48px;
    width: 100%;
  }
`
const ConfirmHeard = styled.div`
  margin: 14px auto 0;
  max-width: 560px;
  text-align: left;

  .ch {
    font-size: 13px;
    font-weight: 600;
    color: ${TOKEN.textMuted};
    margin-bottom: 10px;
  }
  .heard {
    background: ${TOKEN.bgSurface};
    border: 1px solid ${TOKEN.borderDefault};
    border-radius: ${TOKEN.radiusMd};
    padding: 18px 20px;
    font-size: 15px;
    line-height: 1.65;
    color: ${TOKEN.textBody};
  }
  .rr {
    font-size: 12.5px;
    color: ${TOKEN.textDisabled};
    margin: 14px 0 0;
  }
`
const RailLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 4px;
`
const RailRow = styled.div`
  margin-bottom: 16px;
`
const RailName = styled.div`
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
`
const RailFoot = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
`
const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`
const StepBubble = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  ${(p) => {
    if (p.status === 'done') return 'background: #10b981; color: #fff;'
    if (p.status === 'active') return 'background: #4f46e5; color: #fff;'
    return 'background: #f1f5f9; color: #94a3b8;'
  }}
`
const StepText = styled.div`
  flex: 1;
`
const StepName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.status === 'pending' ? '#94a3b8' : '#0f172a')};
`
const StepHint = styled.div`
  font-size: 12px;
  color: ${(p) => (p.status === 'active' ? '#4f46e5' : '#94a3b8')};
  margin-top: 2px;
`
/* legacy RecDot definition replaced by the design-system variant below */

// ---- Zoom-style tile layout -----------------------------------------
const TileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;

  /* On mobile, drop the Zoom-style video tiles entirely and fall back to
     the cleaner old layout (just the Question card + Response card). The
     candidate state is communicated inline via the response card text. */
  @media (max-width: 768px) {
    display: none;
  }
`
const Tile = styled.div`
  position: relative;
  background: #1f2937;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  max-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  @media (max-width: 640px) {
    max-height: 280px;
  }
`
const TileLabel = styled.div`
  position: absolute;
  left: 10px;
  bottom: 10px;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.02em;
  z-index: 2;
`
const SelfVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
  background: #101016;
`
const SelfCameraPip = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 280px;
  height: 210px;
  border-radius: ${TOKEN.radiusMd};
  overflow: hidden;
  background: radial-gradient(120% 120% at 50% 20%, #23242E, #101016);
  box-shadow: ${TOKEN.shadowLg};
  border: 1px solid rgba(255, 255, 255, 0.12);
  z-index: 9000;

  @media (max-width: 640px) {
    width: 148px;
    height: 111px;
    right: 14px;
    bottom: 14px;
  }
`
const PipPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  background: radial-gradient(120% 120% at 50% 20%, #23242E, #101016);
  pointer-events: none;
`
const PipLabel = styled.div`
  position: absolute;
  bottom: 9px;
  left: 10px;
  background: rgba(0, 0, 0, 0.46);
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: ${TOKEN.radiusSm};
  pointer-events: none;

  @media (max-width: 640px) {
    font-size: 10px;
    padding: 2px 7px;
    bottom: 7px;
    left: 7px;
  }
`
const PipRecBadge = styled.div`
  position: absolute;
  top: 9px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: rgba(226, 59, 74, 0.92);
  padding: 3px 8px;
  border-radius: ${TOKEN.radiusSm};
  pointer-events: none;

  .d {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #fff;
    animation: ${blink} 1.4s infinite;
  }

  @media (max-width: 640px) {
    font-size: 9px;
    padding: 2px 6px;
    top: 7px;
    right: 7px;
  }
`
const RecDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${TOKEN.error};
  margin-right: 8px;
  animation: ${blink} 1.4s ease-in-out infinite;
`
const CameraOffOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  background: #111827;
  color: #94a3b8;
  font-size: 13px;
`
const AvatarTileInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
`
const VerticalWave = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  height: 56px;
  span {
    display: block;
    width: 5px;
    background: ${(p) => (p.listening ? '#10b981' : (p.speaking ? '#a5b4fc' : '#475569'))};
    border-radius: 3px;
    transition: background 0.25s ease;
  }
  ${(p) => (p.speaking || p.listening) && css`
    span { animation: ${wave} 1.2s ease-in-out infinite; }
  `}
  span:nth-child(1) { height: 30%; animation-delay: 0s; }
  span:nth-child(2) { height: 60%; animation-delay: 0.1s; }
  span:nth-child(3) { height: 90%; animation-delay: 0.2s; }
  span:nth-child(4) { height: 70%; animation-delay: 0.3s; }
  span:nth-child(5) { height: 50%; animation-delay: 0.4s; }
  span:nth-child(6) { height: 80%; animation-delay: 0.5s; }
  span:nth-child(7) { height: 40%; animation-delay: 0.6s; }
  span:nth-child(8) { height: 65%; animation-delay: 0.7s; }
  span:nth-child(9) { height: 55%; animation-delay: 0.8s; }
  ${(p) => !p.speaking && !p.listening && css`
    span { transform: scaleY(0.4); }
  `}
`
const ControlBar = styled.div`
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  background: rgba(15, 23, 42, 0.96);
  padding: 8px 12px;
  border-radius: 999px;
  display: flex;
  gap: 10px;
  align-items: center;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255,255,255,0.08);

  /* Hide the floating control bar on mobile — End Interview is in the
     TopBar menu, mic auto-opens, and the Response card surfaces the rest. */
  @media (max-width: 768px) {
    display: none;
  }
`
const CtrlBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => (p.danger ? '#dc2626' : (p.off ? '#7f1d1d' : 'rgba(255,255,255,0.08)'))};
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease;
  &:hover {
    background: ${(p) => (p.danger ? '#b91c1c' : (p.off ? '#991b1b' : 'rgba(255,255,255,0.16)'))};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .chev {
    font-size: 9px;
    opacity: 0.7;
    margin-left: 2px;
  }

  @media (max-width: 640px) {
    padding: 8px 12px;
    font-size: 12px;
    gap: 6px;
    .label-text { display: ${(p) => (p.danger ? 'inline' : 'none')}; }
    .chev { display: none; }
  }
`
const QuestionHeader = styled.div`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #4f46e5;
  font-weight: 600;
  margin-bottom: 8px;
`

const LANG_TO_STT_CODE = {
  'English': 'en', 'Hinglish': 'hi', 'Hindi': 'hi',
  'Tamil': 'ta', 'Telugu': 'te', 'Kannada': 'kn',
  'Malayalam': 'ml', 'Bengali': 'bn', 'Gujarati': 'gu',
  'Marathi': 'mr', 'Punjabi': 'pa', 'Urdu': 'ur',
}

const STEP_LIVE = 'live'
const STEP_DONE = 'done'
// Single-attempt lockout: startSession returned 409 (already started/completed)
// — e.g. a reload mid-interview. We never re-enter the live flow; show a
// terminal "already attempted" screen instead.
const STEP_BLOCKED = 'blocked'

// Score-anchored verdict. Backend already enforces this in score-final, but
// stale rows or a buggy older payload can still show up — derive a ceiling
// here too so a 0 can never read as Borderline/Fit.
const effectiveVerdict = (score, storedVerdict) => {
  const s = Number(score) || 0
  let ceiling
  if (s < 40) ceiling = 'Not Fit'
  else if (s < 50) ceiling = 'Borderline'
  else if (s < 80) ceiling = 'Fit'
  else ceiling = 'Strong Fit'
  const ranks = { 'Not Fit': 0, 'Borderline': 1, 'Fit': 2, 'Strong Fit': 3 }
  const stored = (storedVerdict || '').trim()
  const r = ranks[stored]
  if (r === undefined || r > ranks[ceiling]) return ceiling
  return stored
}
const MAX_FULLSCREEN_VIOLATIONS = 4

// How this client ended the interview, reported to student-node on completion.
// Must match the values in its helpers/aiInterviewCompletionReason.js — an
// unrecognised value is discarded there and the ending falls back to being
// derived from the session clock.
const COMPLETION_REASON = {
  CANDIDATE_INITIATED: 'candidate_initiated',
  PROCTORING_VIOLATION: 'proctoring_violation',
  TIME_UP: 'time_up',
}

// How often the in-progress recording is pushed to object storage while the
// interview is still running. The audio used to reach the server ONLY from
// finalize(), so a candidate who closed the tab / dropped off mid-interview
// lost the whole recording — it never left that tab's memory. With this,
// whatever is stored is at most one interval behind, so a drop-off costs at
// most ~20s of audio instead of everything.
const SESSION_AUDIO_FLUSH_MS = 20000

// The scoped invite JWT lives in sessionStorage (cleared on tab close), so any
// upload that might be retried after a reopen has to carry its own snapshot.
const readScopedAuthToken = () => {
  if (typeof sessionStorage === 'undefined') return null
  return (
    sessionStorage.getItem('assessment_invite_scoped_jwt') ||
    sessionStorage.getItem('ai_interview_scoped_jwt') ||
    null
  )
}

const ViolationOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 100000; padding: 20px;

  @media (max-width: 640px) {
    padding: 14px;
  }
`
const ViolationCard = styled.div`
  background: #fff; border-radius: 14px; padding: 30px 34px;
  max-width: 540px; width: 100%;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.35);

  @media (max-width: 640px) {
    padding: 22px 18px;
    border-radius: 12px;
  }
`
const ViolationTitle = styled.h2`
  color: #4318ff; font-size: 22px; font-weight: 700; margin-bottom: 12px; text-align: center;
`
const ViolationBody = styled.div`
  color: #475569; font-size: 14px; line-height: 1.65; margin-bottom: 18px; text-align: left;
`
const WarningBox = styled.div`
  background: #fef3c7; border-left: 4px solid #d97706; padding: 12px 14px;
  border-radius: 0 6px 6px 0; color: #78350f; font-size: 13px; margin-bottom: 16px;
`

const DEFAULT_PROGRESS = {
  currentDim: 'communication',
  sections: [
    { id: 'communication', label: 'Communication', asked: 0, total: 2, status: 'active' },
    { id: 'role_fit', label: 'Role Fitment', asked: 0, total: 2, status: 'pending' },
    { id: 'smartness', label: 'Smartness', asked: 0, total: 2, status: 'pending' },
  ],
}

const Interview = ({ assessment, setAssessment, resumeText }) => {
  const navigate = useNavigate()
  // OTP invite candidates have no logged-in portal to return to; logged-in
  // students do. This drives both the completion-screen CTA and where we send
  // the candidate when they leave.
  const isInvite = !!readInviteScopedJwt()
  const [step, setStep] = useState(STEP_LIVE)
  const [sessionId, setSessionId] = useState(null)
  const [question, setQuestion] = useState(null)
  const [progress, setProgress] = useState(DEFAULT_PROGRESS)
  // 1-based index of the question the candidate is on. Increments ONLY on a
  // genuinely new question — not on a "couldn't hear you" retry of the same
  // one. Shown as "Question X of Y" in the top bar.
  const [questionNumber, setQuestionNumber] = useState(1)
  // Pacing banner: shown on the NEXT question once the candidate has run over
  // the LLM's reference answer-time for 2 consecutive questions. Stays up until
  // they answer one within time. Purely informational — never blocks anything.
  const [pacingWarn, setPacingWarn] = useState(false)
  // Placeholder only — startSession returns the admin-configured duration and
  // overwrites this before the clock starts. Matches student-node's own
  // `config.interviewDuration || 1200` fallback so the two clocks can't
  // disagree about when time is up if that value is ever missing.
  const INTERVIEW_DURATION_SECS = 20 * 60 // 20 minutes
  const [remaining, setRemaining] = useState(INTERVIEW_DURATION_SECS)
  const [aiSpeaking, setAiSpeaking] = useState(false)
  const [recording, setRecording] = useState(false)
  const [thinking, setThinking] = useState(true)
  const [thinkingText, setThinkingText] = useState('Starting interview…')
  // 'idle' | 'next' (between-question) | 'finalise' (last question)
  const [thinkingPhase, setThinkingPhase] = useState('idle')
  const [transcriptLine, setTranscriptLine] = useState('')
  const [report, setReport] = useState(null)
  // Candidate identity (resolved by the backend from primary_email → student
  // profile). Shown in the TopBar instead of the assessment/role name — a
  // previous bug had the bar showing the assessment name (e.g. "Customer") as
  // if it were the candidate's name.
  const [candidateName, setCandidateName] = useState('')
  const [sttLanguageCode, setSttLanguageCode] = useState('en')
  // Progressive subtitle for the question — populated word-by-word during TTS.
  const [displayedText, setDisplayedText] = useState('')
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [endingNow, setEndingNow] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [fsViolations, setFsViolations] = useState(0)
  const [showReentryModal, setShowReentryModal] = useState(false)
  const [showViolationModal, setShowViolationModal] = useState(false)
  // Network state — drives the "Waiting for connection…" overlay and gates the
  // VAD / no-speech / elapsed watchdogs so a mid-interview drop can't burn the
  // clock or wrongly finalise. Initialised from navigator.onLine (SSR-safe).
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  )
  const isOfflineRef = useRef(false)
  useEffect(() => { isOfflineRef.current = isOffline }, [isOffline])
  // Seed candidate identity from the assessment prop (instruction.js passes
  // candidateName through once getConfigInfo returns). startSession will also
  // return it and overwrite this. The TopBar must show the candidate's name,
  // not the assessment/role name.
  useEffect(() => {
    if (assessment?.candidateName && !candidateName) {
      setCandidateName(assessment.candidateName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment?.candidateName])
  // The answer the candidate just submitted, held until submitTurn confirms.
  // Lets us re-submit the SAME answer (idempotent on interactionId) after a
  // reconnect instead of losing it. { interactionId, answer } | null.
  const pendingSubmitRef = useRef(null)
  const stepRef = useRef(STEP_LIVE)

  const audioRef = useRef(null)
  // Admin-selected ElevenLabs voice for this interview (set from startSession).
  const voiceIdRef = useRef(null)
  // Every language this interview may ask in, straight from the config. Sent
  // on each TTS call so the backend picks ONE voice provider for the whole
  // sitting rather than per line — a bilingual interview routed per line
  // changes voice partway through.
  const interviewLanguagesRef = useRef([])
  // Language of the question currently on screen; tells that provider what to
  // speak. Tracked in a ref so speak() reads it without every call site
  // having to thread it through.
  const questionLanguageRef = useRef(null)
  const streamRef = useRef(null)
  const elapsedTimerRef = useRef(null)
  // Self-view camera PIP — bottom-right corner during the live interview.
  // Read-only video for the candidate; no toggle, kept simple.
  const selfVideoRef = useRef(null)
  const selfStreamRef = useRef(null)
  const [cameraReady, setCameraReady] = useState(false)
  // Streaming STT refs
  const wsRef = useRef(null)
  const audioCtxRef = useRef(null)
  const workletNodeRef = useRef(null)
  const sourceNodeRef = useRef(null)
  const silentGainRef = useRef(null)
  const finalTranscriptRef = useRef('')
  // Mirrors the live transcript box (including not-yet-finalized interim text).
  // Used as a fallback at submit time so a visibly-captured answer never gets
  // discarded as "couldn't hear you" just because Deepgram hadn't promoted the
  // tail of it to `final` yet.
  const interimTranscriptRef = useRef('')
  // Set when the backend tells us the Deepgram leg died mid-turn
  // (upstream_dropped). The live transcript at that point is truncated —
  // this forces stopRecordAndSubmit to re-transcribe the full recording via
  // batch STT instead of trusting the partial live one.
  const sttStreamBrokenRef = useRef(false)
  // MediaRecorder fallback — runs in parallel with WS streaming so we still
  // have a usable audio blob if the WS dies mid-turn or Deepgram disconnects.
  const recorderRef = useRef(null)
  const recorderChunksRef = useRef([])
  // VAD (voice activity detection) — auto-submit after 1.8s silence
  const vadSilenceTimerRef = useRef(null)
  const vadSpeakingRef = useRef(false)
  const vadEverSpokeRef = useRef(false)
  // True once the AudioWorklet VAD has emitted at least one rms reading for the
  // current turn — i.e. VAD is genuinely running. We only trust the "candidate
  // never spoke" auto-advance when VAD is live, so a degraded (no-worklet) turn
  // never cuts off a real answer.
  const vadActiveRef = useRef(false)
  // No-speech watchdog: if the candidate makes no sound at all within
  // NO_SPEECH_TIMEOUT_MS of the mic going live, we re-prompt them once on the
  // same question, and only auto-advance if they're still silent the 2nd time.
  const noSpeechTimerRef = useRef(null)
  // Counts consecutive no-response attempts on the CURRENT question. Reset to 0
  // whenever a new question is shown. 0 → ask them to retry; 1 → advance.
  const noAnswerRetryRef = useRef(0)
  const [candidateSpeaking, setCandidateSpeaking] = useState(false)
  // Proctoring (mirrors RoleBasedassmt): periodic webcam snapshot every 20s.
  const proctorVideoRef = useRef(null)
  const proctorCanvasRef = useRef(null)
  const proctorStreamRef = useRef(null)
  const proctorIntervalRef = useRef(null)
  const proctorActiveRef = useRef(false)
  const proctorCapturingRef = useRef(false)
  // Report-only proctoring collector (MediaPipe gaze + head-pitch fusion) mounted
  // on the hidden proctor video, scoped per interview TURN. Combined with the
  // Deepgram WPM signal, this catches reading-an-answer-aloud.
  const proctoringCollector = useProctoringCollector()
  const collectorStartedRef = useRef(false)
  // Per-turn answer-timing + speech stats for the WPM / read-from-source signals.
  const speechStatsRef = useRef(null) // latest Deepgram {wordCount, spokenSec, ...}
  const micOpenedAtRef = useRef(0) // ms epoch the answer mic went live
  const firstWordAtRef = useRef(0) // ms epoch the candidate first spoke this turn
  // Pacing nudge: the LLM's expected answer time (sec) for the CURRENT question,
  // and how many questions in a row the candidate has run over it. Purely an
  // informational nudge — never blocks or skips a question.
  const referenceTimeSecRef = useRef(null)
  const slowAnswerStreakRef = useRef(0)
  const turnIndexRef = useRef(0) // monotonic turn counter for collector scoping
  // Tier-2 adaptive snapshot burst window (epoch ms) — mirrors aptitude.
  const snapshotBurstUntilRef = useRef(0)

  // Camera guard: the interview is always proctored. If the camera turns off
  // during the interview, silently try to turn it back on in the background —
  // no popup, no pause.
  const { cameraLost: camGuardLost } = useCameraGuard({
    enabled: step === STEP_LIVE,
    getStream: () => proctorStreamRef.current || (proctorVideoRef.current && proctorVideoRef.current.srcObject) || null,
  })

  const studentData = useSelector((state) =>
    state.studentData?.studentDetails || state.auth?.user || null
  )

  // Fullscreen lockdown is only enforceable on devices that actually support
  // element fullscreen (desktop / macOS Safari / iPad). iPhone Safari cannot
  // fullscreen a web element, so there we run proctoring in "lite" mode:
  // camera snapshots, mic/STT streaming and tab/app-switch detection stay ON,
  // but the fullscreen request and fullscreen-exit violations are skipped.
  const enforceFullscreen = shouldEnforceFullscreen()

  // Keep refs in sync so setTimeout closures don't read stale state.
  useEffect(() => { stepRef.current = step }, [step])

  // Self-view camera PIP: start the camera when we enter STEP_LIVE, stop
  // it when we leave (STEP_DONE) or unmount. Independent of the mic
  // capture so it keeps running even while we're not recording.
  useEffect(() => {
    if (step !== STEP_LIVE) {
      if (selfStreamRef.current) {
        selfStreamRef.current.getTracks().forEach((t) => t.stop())
        selfStreamRef.current = null
      }
      if (selfVideoRef.current) selfVideoRef.current.srcObject = null
      setCameraReady(false)
      return undefined
    }
    let cancelled = false
    ;(async () => {
      try {
        const stream = await getUserMediaWithSelectedDevices({
          video: { width: 640, height: 480, facingMode: 'user' },
          audio: false,
        })
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
        selfStreamRef.current = stream
        if (selfVideoRef.current) {
          selfVideoRef.current.srcObject = stream
          selfVideoRef.current.onplaying = () => setCameraReady(true)
          selfVideoRef.current.play().catch(() => {})
        }
      } catch (err) {
        console.warn('[self-cam] could not start:', err?.message)
      }
    })()
    return () => {
      cancelled = true
      if (selfStreamRef.current) {
        selfStreamRef.current.getTracks().forEach((t) => t.stop())
        selfStreamRef.current = null
      }
      setCameraReady(false)
    }
  }, [step])
  const recordingRef = useRef(false)
  useEffect(() => { recordingRef.current = recording }, [recording])
  const sttLanguageCodeRef = useRef('en')
  useEffect(() => { sttLanguageCodeRef.current = sttLanguageCode }, [sttLanguageCode])
  // A Hinglish interview switches to Hindi or English mid-way when the candidate
  // says they can't follow the mix; the backend re-asks the current question in
  // that language and sends `languageSwitch` alongside it.
  //
  // The ref assignment is the load-bearing one, not setState: the STT websocket
  // is opened fresh for each answer off sttLanguageCodeRef.current, and the
  // state->ref effect won't have run by the time the candidate starts speaking,
  // so writing only the state would leave the next recording on the old
  // provider. TTS needs nothing — it's a multilingual ElevenLabs voice keyed by
  // voice id, not by language.
  const applyLanguageSwitch = (languageSwitch) => {
    const to = languageSwitch?.to
    if (!to) return
    const code = LANG_TO_STT_CODE[to] || 'en'
    sttLanguageCodeRef.current = code
    setSttLanguageCode(code)
    message.info(`Continuing in ${to}.`)
  }
  const aiSpeakingRef = useRef(false)
  useEffect(() => { aiSpeakingRef.current = aiSpeaking }, [aiSpeaking])
  const thinkingRef = useRef(false)
  useEffect(() => { thinkingRef.current = thinking }, [thinking])
  // Timer for word-by-word subtitle reveal during TTS playback.
  const revealTimerRef = useRef(null)
  // Monotonic counter — every speak() call bumps this; in-flight calls
  // bail out if their seq is stale, so we never get two TTS audios playing.
  const speakSeqRef = useRef(0)
  // Chapter markers for the admin-side "jump to question" audio player —
  // one entry per genuine question (not retry apologies), with its offset
  // into the session recording. Sent up once at finalize().
  const chaptersRef = useRef([])
  const chapterCountRef = useRef(0)
  // Ref-dispatcher for the auto-submit callback. handleVadMessage is captured
  // when the audio worklet is wired up (inside startRecord), which itself
  // runs from a stale mount-time closure. Calling submit through this ref
  // ensures we always invoke the latest stopRecordAndSubmit, so it sees the
  // current sessionId / question / recording state — not the mount snapshot.
  const submitTurnFnRef = useRef(() => {})
  // One turn can be closed by the Done button and the interview timer at nearly
  // the same instant. Both callers must await the same save instead of stopping
  // the recorder twice or submitting the interaction twice.
  const turnSubmitInFlightRef = useRef(null)
  const interviewEndingRequestedRef = useRef(false)
  // Same pattern for finalize(): the elapsed-timer interval and the
  // fullscreen-violation modal both fire from setInterval/setTimeout
  // callbacks captured at mount time, when sessionId is still null. If
  // they call finalize() directly, the in-closure sessionId is null and
  // completeSession 400s — the row stays INPROGRESS even though the
  // candidate sees the done screen.
  const finalizeFnRef = useRef(() => {})
  const finalizeInFlightRef = useRef(null)
  // Set synchronously the moment finalize() starts. The periodic flush checks
  // it so a partial recording can never be uploaded (and overwrite the final,
  // complete one — they share an object key) after the interview has ended.
  const interviewEndedRef = useRef(false)
  // Promise for the flush currently in flight, so finalize() can wait for it to
  // settle before writing the complete recording. Without this a partial PUT
  // that started just before finalize could land AFTER the final PUT and
  // truncate the stored audio.
  const flushInFlightRef = useRef(null)

  // Rotate the status message every ~1.6s so the wait after "Done answering"
  // feels active instead of frozen. Fires for the between-question pause
  // ('next') and the final wrap-up ('finalise') — each with its own copy so the
  // candidate always sees the interviewer "doing something" for the few seconds
  // it takes to evaluate the answer and prepare the next step.
  useEffect(() => {
    if (!thinking || (thinkingPhase !== 'next' && thinkingPhase !== 'finalise')) return undefined
    // Mixes "we're doing X" reassurance with light one-liners so the candidate
    // isn't staring at a static "Thinking…" for several seconds straight.
    const NEXT_STAGES = [
      'Listening to your answer…',
      'Evaluating your response…',
      'Mulling that over…',
      'Noting the key points…',
      'Skimming notes from earlier…',
      'Cooking up the next question…',
      'Picking the next angle…',
      'Sharpening the question…',
      'Almost there…',
    ]
    const FINALISE_STAGES = [
      'Evaluating your responses…',
      'Reviewing the full conversation…',
      'Weighing your answers…',
      'Putting together your summary…',
      'Finishing up — almost done…',
    ]
    const stages = thinkingPhase === 'finalise' ? FINALISE_STAGES : NEXT_STAGES
    // Shuffle so candidates who submit several answers in a row don't read the
    // exact same script every time. Math.random only affects copy, nothing
    // business-critical.
    const order = stages
      .map((s) => ({ s, k: Math.random() }))
      .sort((a, b) => a.k - b.k)
      .map((o) => o.s)
    let i = 0
    setThinkingText(order[0])
    const id = setInterval(() => {
      i = Math.min(i + 1, order.length - 1)
      setThinkingText(order[i])
    }, 1600)
    return () => clearInterval(id)
  }, [thinking, thinkingPhase])

  // Fullscreen exit + tab-switch monitoring (proctoring).
  useEffect(() => {
    const VIOLATION_DEBOUNCE_MS = 1500
    let lastViolationAt = 0

    const isFs = () => Boolean(
      document.fullscreenElement || document.webkitFullscreenElement ||
      document.mozFullScreenElement || document.msFullscreenElement
    )

    const recordViolation = () => {
      if (stepRef.current !== STEP_LIVE) return
      const now = Date.now()
      if (now - lastViolationAt < VIOLATION_DEBOUNCE_MS) return
      lastViolationAt = now
      setFsViolations((n) => {
        const next = n + 1
        if (next >= MAX_FULLSCREEN_VIOLATIONS) {
          setShowViolationModal(true)
          setTimeout(() => {
            try { finalizeFnRef.current(COMPLETION_REASON.PROCTORING_VIOLATION) } catch (_) {}
          }, 5000)
        } else {
          setShowReentryModal(true)
        }
        return next
      })
    }

    const handleFs = () => {
      if (stepRef.current !== STEP_LIVE) return
      if (!isFs()) {
        // Report-only: feed the exit into the proctoring report (separate from
        // the existing in-exam warning/auto-finalize flow below).
        if (collectorStartedRef.current) {
          proctoringCollector.recordEvent('fullscreen_exit', { severity: 'medium', confidence: 1 })
        }
        recordViolation()
      } else {
        // Returned to fullscreen — clear the reentry modal.
        setShowReentryModal(false)
      }
    }

    const handleVis = () => {
      if (stepRef.current !== STEP_LIVE) return
      if (document.visibilityState === 'hidden') {
        // Report-only: a hidden tab during an interview is the same-device
        // lookup gaze can't see. Tee it in; warning flow is unchanged.
        if (collectorStartedRef.current) {
          proctoringCollector.recordEvent('tab_hidden', { severity: 'high', confidence: 1 })
        }
        recordViolation()
      }
    }

    // Tab/app-switch detection runs on every device (incl. iPhone LITE mode).
    document.addEventListener('visibilitychange', handleVis)
    // Fullscreen-exit violations are only registered where fullscreen is
    // actually enforceable. On iPhone (LITE) the OS can't hold a web element in
    // fullscreen, so leaving "fullscreen" must never count as a violation.
    if (enforceFullscreen) {
      document.addEventListener('fullscreenchange', handleFs)
      document.addEventListener('webkitfullscreenchange', handleFs)
      document.addEventListener('mozfullscreenchange', handleFs)
      document.addEventListener('MSFullscreenChange', handleFs)
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVis)
      if (enforceFullscreen) {
        document.removeEventListener('fullscreenchange', handleFs)
        document.removeEventListener('webkitfullscreenchange', handleFs)
        document.removeEventListener('mozfullscreenchange', handleFs)
        document.removeEventListener('MSFullscreenChange', handleFs)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enforceFullscreen])

  // Expose a re-enter handler the modal can call.
  const reenterFullscreen = async () => {
    try {
      const el = document.documentElement
      if (el.requestFullscreen) await el.requestFullscreen()
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen()
      else if (el.mozRequestFullScreen) await el.mozRequestFullScreen()
      setShowReentryModal(false)
    } catch (_) { /* swallow */ }
  }

  // ---- Proctoring snapshots: mirrors RoleBasedassmt pattern -----------
  // Periodically (every 20s) capture a webcam frame and upload it as a JPEG
  // to the same endpoint other assessment types use. Best-effort: never blocks
  // the interview if the camera is denied or upload fails.
  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result.split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

  // Resolve the (assessmentAssignedId, studentId) pair used by every proctoring
  // call. OTP-invite candidates have no studentData, so fall back to the invite
  // assigned-id from sessionStorage (mirrors uploadProctorFrame's resolution).
  const resolveProctorIds = () => {
    const assessmentAssignedId = assessment?.assessment_assigned_id
    const otpAssignedId =
      (typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem('assessment_invite_assigned_id') ||
          sessionStorage.getItem('ai_interview_assessment_assigned_id'))) || null
    const studentId =
      studentData?.id || studentData?.studentId || studentData?.student_id ||
      otpAssignedId || assessmentAssignedId
    return { assessmentAssignedId, studentId }
  }

  // Start the MediaPipe gaze collector once, on the live proctor video. Scoped
  // per turn via setActiveQuestion (called when each answer mic opens). Reuses
  // the same report-only events pipeline as aptitude; no-op without an id.
  const startCollectorIfReady = () => {
    if (collectorStartedRef.current) return
    const { assessmentAssignedId, studentId } = resolveProctorIds()
    if (!assessmentAssignedId || !proctorVideoRef.current) return
    collectorStartedRef.current = true
    proctoringCollector.start({
      videoEl: proctorVideoRef.current,
      assessmentAssignedId,
      studentId,
      sessionId: null,
      onSuspicion: () => { snapshotBurstUntilRef.current = Date.now() + 30000 },
    })
    // Detect a second monitor at start (answers shown on the other screen).
    try {
      if (window.screen && window.screen.isExtended === true) {
        proctoringCollector.recordEvent('second_display', { severity: 'high', confidence: 0.8 })
      }
    } catch (_) { /* permission denied / unsupported → leave unflagged */ }
  }

  // Silently re-acquire the proctor camera in the background — no popup, no pause.
  const reacquiringCameraRef = useRef(false)
  const reacquireCameraSilently = useCallback(async () => {
    if (reacquiringCameraRef.current) return
    reacquiringCameraRef.current = true
    try {
      try {
        if (proctorStreamRef.current) proctorStreamRef.current.getTracks().forEach((t) => { try { t.stop() } catch (_) {} })
      } catch (e) { /* noop */ }
      proctorStreamRef.current = null
      proctorVideoRef.current = null
      const b = await captureProctorFrame()
      if (b) await uploadProctorFrame(b)
    } catch (e) {
      console.warn('[camera-guard] silent re-acquire failed:', e?.message || e)
    } finally {
      reacquiringCameraRef.current = false
    }
  }, [])

  // While the camera is lost mid-interview, keep retrying silently in the
  // background until it's back — the interview keeps running uninterrupted.
  useEffect(() => {
    if (!camGuardLost) return undefined
    reacquireCameraSilently()
    const id = setInterval(reacquireCameraSilently, 5000)
    return () => clearInterval(id)
  }, [camGuardLost, reacquireCameraSilently])

  const captureProctorFrame = async () => {
    if (proctorCapturingRef.current) return null
    proctorCapturingRef.current = true
    const safety = setTimeout(() => { proctorCapturingRef.current = false }, 15000)
    try {
      if (!proctorVideoRef.current) {
        try {
          const stream = await getUserMediaWithSelectedDevices({
            video: { width: 640, height: 480 },
            audio: false,
          })
          proctorStreamRef.current = stream
          proctorVideoRef.current = document.createElement('video')
          mountHiddenProctorVideo(proctorVideoRef.current)
          watchProctorTrack(stream, () => {
            unmountProctorVideo(proctorVideoRef.current)
            proctorVideoRef.current = null
          })
          proctorVideoRef.current.srcObject = stream
          proctorVideoRef.current.autoplay = true
          proctorVideoRef.current.muted = true
          proctorVideoRef.current.playsInline = true
          await new Promise((resolve) => {
            proctorVideoRef.current.onloadedmetadata = () => {
              proctorVideoRef.current.play().then(resolve).catch(resolve)
            }
            setTimeout(resolve, 1000)
          })
          proctorCanvasRef.current = document.createElement('canvas')
          proctorCanvasRef.current.width = 640
          proctorCanvasRef.current.height = 480
          proctorActiveRef.current = true
          // Stream + <video> are live — start the report-only gaze collector once,
          // scoped per turn. No-op + no CDN load if it can't resolve an id.
          startCollectorIfReady()
        } catch (err) {
          console.warn('[proctoring] camera init failed:', err?.message || err)
          clearTimeout(safety)
          proctorCapturingRef.current = false
          return null
        }
      }
      const ctx = proctorCanvasRef.current.getContext('2d')
      ctx.drawImage(
        proctorVideoRef.current,
        0, 0,
        proctorCanvasRef.current.width,
        proctorCanvasRef.current.height,
      )
      const blob = await new Promise((res) =>
        proctorCanvasRef.current.toBlob(res, 'image/jpeg', 0.8)
      )
      clearTimeout(safety)
      proctorCapturingRef.current = false
      return blob
    } catch (err) {
      console.warn('[proctoring] capture failed:', err?.message || err)
      clearTimeout(safety)
      proctorCapturingRef.current = false
      return null
    }
  }

  const uploadProctorFrame = async (blob) => {
    if (!blob) return
    const assessmentAssignedId = assessment?.assessment_assigned_id
    const otpAssignedId =
      (typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem('assessment_invite_assigned_id') ||
          sessionStorage.getItem('ai_interview_assessment_assigned_id'))) || null
    const studentId =
      studentData?.id || studentData?.studentId || studentData?.student_id ||
      otpAssignedId || assessmentAssignedId
    if (!assessmentAssignedId || !studentId) return
    try {
      const base64 = await blobToBase64(blob)
      const bytes = atob(base64)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
      const file = new Blob([arr], { type: 'image/jpeg' })
      const form = new FormData()
      // Text fields BEFORE the file — fastify-multipart on the server only
      // sees fields parsed before the file boundary in the multipart
      // stream when read via req.file(). Keeping IDs first avoids the
      // intermittent PROD 400 ("assessmentAssignedId is required").
      form.append('assessmentAssignedId', assessmentAssignedId)
      form.append('studentId', studentId)
      form.append('image', file)
      await studentRequest.post(
        'students/assessments/proctoring/uploadImage',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
    } catch (err) {
      console.warn('[proctoring] upload failed:', err?.message || err)
    }
  }

  useEffect(() => {
    if (step !== STEP_LIVE) return undefined
    const assessmentAssignedId = assessment?.assessment_assigned_id
    const otpAssignedId =
      (typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem('assessment_invite_assigned_id') ||
          sessionStorage.getItem('ai_interview_assessment_assigned_id'))) || null
    const studentId =
      studentData?.id || studentData?.studentId || studentData?.student_id ||
      otpAssignedId || assessmentAssignedId
    if (!assessmentAssignedId || !studentId) return undefined

    proctorActiveRef.current = true
    // Adaptive cadence: 20s normally, 5s during a suspicion burst window (set by
    // the collector's onSuspicion). Self-rescheduling timeout so the next delay
    // can adapt; proctorIntervalRef holds the timeout id.
    const SNAP_MS = 20000
    const SNAP_BURST_MS = 5000
    const snapOnce = async () => {
      if (proctorActiveRef.current) {
        const b = await captureProctorFrame()
        if (b) await uploadProctorFrame(b)
      }
      const inBurst = Date.now() < snapshotBurstUntilRef.current
      proctorIntervalRef.current = setTimeout(snapOnce, inBurst ? SNAP_BURST_MS : SNAP_MS)
    }
    // Fire one capture immediately, then on the adaptive schedule.
    snapOnce()

    return () => {
      proctorActiveRef.current = false
      if (proctorIntervalRef.current) {
        clearTimeout(proctorIntervalRef.current)
        proctorIntervalRef.current = null
      }
      // Flush + stop the gaze collector so its final events are ingested.
      if (collectorStartedRef.current) {
        try { proctoringCollector.stop() } catch (_) {}
        collectorStartedRef.current = false
      }
      if (proctorStreamRef.current) {
        try { proctorStreamRef.current.getTracks().forEach((t) => t.stop()) } catch (_) {}
        proctorStreamRef.current = null
      }
      proctorVideoRef.current = null
      proctorCanvasRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, assessment?.assessment_assigned_id, studentData?.id, studentData?.studentId, studentData?.student_id])

  // ---- Mount: start session
  useEffect(() => {
    // Register the durable-upload resume triggers ASAP on entry so a pending
    // recording from a previous (reloaded/closed) session gets re-uploaded
    // even if this session never reaches finalize().
    initUploadQueueResumption()
    let active = true
    ;(async () => {
      try {
        const { data } = await withReconnect(
          () => startSession(assessment?.assessment_assigned_id, resumeText || null),
          { onWait: () => { setThinkingText('Waiting for connection…') } }
        )
        if (!active) return
        const d = data?.data || {}
        setSessionId(d.sessionId)
        voiceIdRef.current = d.voiceId || null
        interviewLanguagesRef.current = Array.isArray(d.interviewLanguages) ? d.interviewLanguages : []
        questionLanguageRef.current = d.question?.questionLanguage || null
        if (d.candidateName) setCandidateName(d.candidateName)
        if (d.interviewDuration) setRemaining(d.interviewDuration)
        const respLang = d.responderLanguage || 'English'
        setSttLanguageCode(LANG_TO_STT_CODE[respLang] || 'en')
        setQuestion(d.question)
        setQuestionNumber(1)
        referenceTimeSecRef.current = d.question?.referenceTimeSec ?? null
        slowAnswerStreakRef.current = 0
        setPacingWarn(false)
        noAnswerRetryRef.current = 0
        if (d.progress) setProgress(d.progress)
        setThinking(false)
        speak(d.question?.text, { speechText: d.question?.speechText })
        startElapsedTimer()
      } catch (err) {
        console.error(err)
        if (!active) return
        // 409 = single-attempt lockout (already started or completed). Don't
        // re-enter the live flow or let the candidate retake — show the
        // terminal "already attempted" screen. Persist the local completed flag
        // so a further reload of the standalone route also lands here.
        if (err?.response?.status === 409) {
          markAiInterviewCompleted(assessment?.assessment_assigned_id)
          stepRef.current = STEP_BLOCKED
          setStep(STEP_BLOCKED)
          setThinking(false)
          return
        }
        message.error(err?.response?.data?.message || 'Failed to start interview')
        setThinking(false)
      }
    })()
    return () => {
      active = false
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current)
      if (revealTimerRef.current) {
        try { clearInterval(revealTimerRef.current) } catch (_) {}
        revealTimerRef.current = null
      }
      if (audioRef.current) {
        try { audioRef.current.pause() } catch (_) {}
      }
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach((t) => t.stop()) } catch (_) {}
      }
      // Best-effort cleanup only — if finalize() already ran it already
      // stopped+uploaded+torn down (this becomes a harmless no-op). If the
      // candidate closed the tab mid-interview there's no time to upload; just
      // release the AudioContext so it isn't leaked.
      teardownSessionRecording()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Push the recording-so-far to object storage without stopping the recorder,
  // overwriting the previous partial (stable object key). Self-healing: a
  // failed flush needs no retry logic because the next tick sends a strictly
  // longer recording to the same key.
  const flushSessionAudio = useCallback(async () => {
    const assignedId = assessment?.assessment_assigned_id
    // Skip once finalize() owns the upload, and never overlap two flushes —
    // uploads grow over the interview and could otherwise land out of order.
    if (!assignedId || interviewEndedRef.current || flushInFlightRef.current) return
    const snapshot = snapshotSessionRecording()
    if (!snapshot?.blob) return
    const upload = uploadSessionAudio(
      assignedId, snapshot.blob, snapshot.mimeType, chaptersRef.current,
    ).catch((e) => {
      // Non-fatal by design — the candidate must never see or be blocked by a
      // flush failure; the next tick re-sends a more complete recording.
      console.warn('[session-recorder] periodic flush failed (will retry):', e?.message || e)
    })
    flushInFlightRef.current = upload
    try {
      await upload
    } finally {
      if (flushInFlightRef.current === upload) flushInFlightRef.current = null
    }
  }, [assessment?.assessment_assigned_id])

  // Runs for the whole mount: snapshotSessionRecording() returns null before
  // the recorder has buffered anything and after teardown clears it, so ticks
  // outside the live interview are cheap no-ops.
  useEffect(() => {
    const id = setInterval(() => { flushSessionAudio() }, SESSION_AUDIO_FLUSH_MS)
    return () => clearInterval(id)
  }, [flushSessionAudio])

  // Safety net for the final sub-interval on a tab close / navigation away.
  // Best-effort ONLY: the page is being discarded, so an async upload would be
  // killed in flight — we persist to IndexedDB instead and let the queue's
  // resume triggers ship it if the candidate opens the app again. The periodic
  // flush above is what actually guarantees the audio reaches the server; this
  // just recovers the tail recorded since the last flush.
  useEffect(() => {
    const onPageHide = () => {
      const assignedId = assessment?.assessment_assigned_id
      // After finalize() the queue already holds the complete recording —
      // never let a partial overwrite it (both key on assessmentAssignedId).
      if (!assignedId || interviewEndedRef.current) return
      const snapshot = snapshotSessionRecording()
      if (!snapshot?.blob) return
      enqueueRecording({
        assessmentAssignedId: assignedId,
        blob: snapshot.blob,
        mimeType: snapshot.mimeType,
        chapters: chaptersRef.current,
        authToken: readScopedAuthToken(),
      })
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [assessment?.assessment_assigned_id])

  const startElapsedTimer = () => {
    elapsedTimerRef.current = setInterval(() => {
      // Freeze the clock while offline — a network drop shouldn't eat the
      // candidate's time or trip the auto-finalize at 0.
      if (isOfflineRef.current) return
      setRemaining((s) => {
        if (s <= 1) {
          // Time's up — auto-finalize. Dispatch through finalizeFnRef so
          // we hit the latest finalize() closure (which has the real
          // sessionId). Calling the mount-time finalize directly would
          // see sessionId=null and the row would stay INPROGRESS.
          clearInterval(elapsedTimerRef.current)
          elapsedTimerRef.current = null
          try { finalizeFnRef.current(COMPLETION_REASON.TIME_UP) } catch (_) {}
          return 0
        }
        return s - 1
      })
      return // keep the old setElapsed call structure
    }, 1000)
  }

  const fmtTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0')
    const r = (s % 60).toString().padStart(2, '0')
    return `${m}:${r}`
  }

  const clearRevealTimer = () => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current)
      revealTimerRef.current = null
    }
  }

  // Treat network/5xx/timeouts as transient. A request that reaches the server
  // and gets a real 4xx (e.g. 409 already-completed) is NOT retried.
  const isTransientErr = (err) => {
    const status = err?.response?.status
    if (status && status >= 400 && status < 500) return false
    return true // no response (offline), 5xx, timeout, ECONNABORTED, etc.
  }

  // Await an online connection (resolves immediately if already online). Used
  // to hold a retry loop until the network is actually back rather than burning
  // attempts into the void.
  const waitForOnline = () =>
    new Promise((resolve) => {
      if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        resolve()
        return
      }
      const onUp = () => {
        window.removeEventListener('online', onUp)
        resolve()
      }
      window.addEventListener('online', onUp)
    })

  // Retry wrapper for the interview's critical network calls (submitTurn,
  // startSession, completeSession). Retries transient failures with capped
  // exponential backoff, pausing on each attempt until the browser is online,
  // so a mid-interview disconnect resumes automatically when the net returns
  // instead of dead-ending. `onWait` lets the caller surface a status message.
  const withReconnect = async (fn, { attempts = 6, baseMs = 1000, maxMs = 8000, onWait } = {}) => {
    let lastErr
    for (let i = 0; i < attempts; i += 1) {
      try {
        await waitForOnline()
        return await fn()
      } catch (err) {
        lastErr = err
        if (!isTransientErr(err) || i === attempts - 1) throw err
        if (onWait) { try { onWait(i + 1) } catch (_) {} }
        const delay = Math.min(maxMs, baseMs * 2 ** i)
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, delay))
      }
    }
    throw lastErr
  }

  const autoStartMicIfReady = () => {
    // After the AI finishes speaking, automatically open the mic so the
    // candidate can just answer — no extra click needed.
    setTimeout(() => {
      if (stepRef.current !== STEP_LIVE) return
      if (recordingRef.current) return
      if (aiSpeakingRef.current) return
      if (thinkingRef.current) return
      startRecord().catch((err) => console.warn('auto-mic failed:', err?.message || err))
    }, 350)
  }

  const stopCurrentAudio = () => {
    // Hard-stop any in-flight TTS audio so a new question can't overlap.
    const prior = audioRef.current
    if (prior) {
      try { prior.onended = null } catch (_) {}
      try { prior.onerror = null } catch (_) {}
      try { prior.onloadedmetadata = null } catch (_) {}
      try { prior.pause() } catch (_) {}
      try { prior.src = '' } catch (_) {}
      try { prior.load() } catch (_) {}
    }
    audioRef.current = null
  }

  const speak = async (text, opts = {}) => {
    // opts.keepQuestion: when set (the "couldn't hear you" retry), the spoken
    // text is just an apology — once it finishes we restore the ORIGINAL
    // question to the screen so the candidate sees the same question to answer,
    // instead of the apology lingering as the on-screen text.
    const keepQuestion = opts.keepQuestion || ''
    const speechText = opts.speechText || text
    // Every call gets a unique seq; if a newer speak() runs before our TTS
    // resolves, we abandon this one before instantiating the audio element.
    const mySeq = ++speakSeqRef.current
    // Always tear down any audio + reveal timer from the previous question
    // before we start. This is the single source of truth that prevents
    // overlap when speak() is invoked back-to-back.
    stopCurrentAudio()
    clearRevealTimer()
    if (!text) {
      setAiSpeaking(false)
      autoStartMicIfReady()
      return
    }
    // Wire the interviewer's voice into the session recording BEFORE this
    // question plays. Previously this only happened in startRecord() once the
    // candidate's mic opened — which fires AFTER the AI finishes speaking, so
    // question 1's audio was always missed (later questions worked because the
    // tap was already wired from the prior turn). micStream is omitted here
    // since it may not be acquired yet; startRecord() attaches it once ready.
    startSessionRecording({ ttsAudioEl: getTtsAudio(), mimeCandidate: pickAudioMimeType() })
    // Drop a chapter marker for genuine questions only — not the "couldn't
    // hear you" retry apology (keepQuestion set), which re-asks the SAME
    // question and must not appear as a separate entry in the admin player.
    if (!keepQuestion) {
      const offsetMs = getRecordingElapsedMs()
      if (offsetMs !== null) {
        chapterCountRef.current += 1
        chaptersRef.current.push({ n: chapterCountRef.current, offsetMs, text })
      }
    }
    setAiSpeaking(true)
    setDisplayedText('')
    // Pre-tokenise so subtitle reveal is cheap.
    const tokens = String(text).split(/(\s+)/).filter((t) => t.length > 0)
    let revealed = 0
    const startReveal = (durationMs) => {
      clearRevealTimer()
      // Cap at ~180ms/token so first words appear quickly even if duration
      // metadata is missing; floor at 50ms so we don't outpace TTS.
      const perToken = Math.max(50, Math.min(180, durationMs / Math.max(1, tokens.length)))
      revealTimerRef.current = setInterval(() => {
        if (revealed >= tokens.length) {
          clearRevealTimer()
          return
        }
        revealed += 1
        setDisplayedText(tokens.slice(0, revealed).join(''))
      }, perToken)
    }
    try {
      const blob = await tts(
        speechText,
        voiceIdRef.current,
        opts.language || questionLanguageRef.current,
        interviewLanguagesRef.current
      )
      // If another speak() superseded us while waiting for ElevenLabs,
      // drop this audio entirely — the newer call already owns the UI.
      if (mySeq !== speakSeqRef.current) {
        return
      }
      const url = URL.createObjectURL(blob)
      // Reuse the persistent, gesture-unlocked element (see ttsAudio.js) so iOS
      // Safari allows this programmatic play() even though it runs after the
      // `await tts(...)`. Fall back to a fresh element if the singleton is
      // unavailable (non-browser/SSR).
      const audio = getTtsAudio() || new Audio()
      audio.src = url
      audio.muted = false
      audioRef.current = audio
      // Reveal length-estimate fallback: ~220ms per token (~270wpm).
      const fallbackDuration = tokens.length * 220
      await new Promise((resolve) => {
        let resolved = false
        const finish = () => {
          if (resolved) return
          resolved = true
          clearRevealTimer()
          // Snap to full text in case reveal didn't finish.
          setDisplayedText(text)
          resolve()
        }
        audio.onloadedmetadata = () => {
          const d = audio.duration
          const durMs = (d && isFinite(d) && d > 0) ? d * 1000 : fallbackDuration
          startReveal(durMs)
        }
        audio.onended = finish
        audio.onerror = finish
        audio.play().catch(finish)
        // If metadata never fires (some MP3 streams), still start reveal.
        setTimeout(() => {
          if (!revealTimerRef.current && !resolved) startReveal(fallbackDuration)
        }, 250)
      })
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('TTS error', err)
      if (mySeq === speakSeqRef.current) {
        // Show full text if TTS failed entirely.
        setDisplayedText(text)
      }
    } finally {
      // Only the currently-active speak() call should update UI state +
      // auto-open the mic. A stale call leaves the newer one in charge.
      if (mySeq === speakSeqRef.current) {
        clearRevealTimer()
        // Retry case: restore the original question to the screen (the apology
        // was just spoken; the candidate must answer the same question again).
        if (keepQuestion) setDisplayedText(keepQuestion)
        setAiSpeaking(false)
        autoStartMicIfReady()
      }
    }
  }

  // ---- Streaming STT (WebSocket + AudioWorklet) -----------------------
  const openSttSocket = () => new Promise((resolve, reject) => {
    const FASTAPI = process.env.REACT_APP_FASTAPI_URL || ''
    const langCode = sttLanguageCodeRef.current || 'en'
    const wsUrl = (FASTAPI.replace(/^http/, 'ws') || 'ws://localhost:8011') + '/ai-interview/stt-stream?language=' + encodeURIComponent(langCode)
    const ws = new WebSocket(wsUrl)
    ws.binaryType = 'arraybuffer'
    let opened = false
    ws.onopen = () => {}
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data)
        if (m.type === 'open') { opened = true; resolve(ws); return }
        if (m.type === 'error') { console.warn('[stt-stream]', m.message); return }
        if (m.type === 'upstream_dropped') { sttStreamBrokenRef.current = true; console.warn('[stt-stream] upstream dropped mid-turn'); return }
        // Show live running transcript as the candidate speaks.
        if (m.running !== undefined) { setTranscriptLine(m.running); interimTranscriptRef.current = m.running }
        if (m.type === 'final' && m.running) finalTranscriptRef.current = m.running
        if (m.type === 'done' && m.running) finalTranscriptRef.current = m.running
        // Capture Deepgram word-timing stats for accurate WPM (real spoken
        // duration, immune to think-time). Latest wins; 'done' is authoritative.
        if (m.speech && (m.speech.wordCount || 0) > 0) speechStatsRef.current = m.speech
      } catch (_) { /* non-JSON ignored */ }
    }
    ws.onerror = (e) => { if (!opened) reject(e) }
    ws.onclose = () => { if (!opened) reject(new Error('socket closed before open')) }
    // Open timeout — if the WS isn't up in this window the candidate is on a
    // restricted network and we fall back to MediaRecorder batch STT rather
    // than blocking the mic-open UI behind it. 3s was too tight (a cold WSS
    // handshake over a slow mobile link routinely took 3-5s, so the live
    // transcript was silently abandoned and the candidate only saw text appear
    // in one shot at "Done" via batch STT). 8s keeps the fallback safety net
    // while letting the live socket actually attach on normal connections.
    setTimeout(() => { if (!opened) reject(new Error('socket open timeout')) }, 8000)
  })

  const installAudioWorklet = async (audioCtx) => {
    const code = PCM_WORKLET_SOURCE
    const url = URL.createObjectURL(new Blob([code], { type: 'application/javascript' }))
    try { await audioCtx.audioWorklet.addModule(url) }
    finally { URL.revokeObjectURL(url) }
  }

  const VAD_SPEECH_THRESHOLD = 0.015
  // Generous silence window so the candidate has room to think mid-answer
  // without the AI cutting in. Deepgram's own utterance_end_ms (1200ms)
  // already breaks the transcript at natural pauses, so this only controls
  // when to auto-submit.
  const VAD_SILENCE_TIMEOUT_MS = 3500
  // If the candidate makes no sound at all in this window after the mic goes
  // live, we treat the turn as a no-response and auto-advance. Kept at 10s so a
  // candidate who just needs a moment to start speaking isn't cut off.
  const NO_SPEECH_TIMEOUT_MS = 10000
  // Sentinel sent when the candidate produced nothing transcribable. The
  // backend recognises this as a non-answer (counts toward dodge/wrap-up) and
  // it advances the interview instead of getting stuck on an empty turn.
  const NO_RESPONSE_MARKER = '[No response]'

  const handleVadMessage = (rms) => {
    // VAD is genuinely running for this turn.
    vadActiveRef.current = true
    const isSpeaking = rms > VAD_SPEECH_THRESHOLD
    if (isSpeaking) {
      vadSpeakingRef.current = true
      vadEverSpokeRef.current = true
      // First detectable speech of this turn → stamp time-to-first-word, which
      // (combined with WPM) separates "thought then spoke naturally" from
      // "sat silent then read a polished answer".
      if (!firstWordAtRef.current) firstWordAtRef.current = Date.now()
      setCandidateSpeaking(true)
      // Candidate made a sound — cancel the no-speech watchdog.
      if (noSpeechTimerRef.current) {
        clearTimeout(noSpeechTimerRef.current)
        noSpeechTimerRef.current = null
      }
      // Clear any pending silence timer — candidate is still talking.
      if (vadSilenceTimerRef.current) {
        clearTimeout(vadSilenceTimerRef.current)
        vadSilenceTimerRef.current = null
      }
    } else if (vadSpeakingRef.current) {
      // Was speaking, now silent — just update the visual state.
      // Auto-submit on silence was removed: the candidate has to click
      // "Done answering" explicitly so a long thinking pause never sends
      // a half-finished answer to the scorer.
      vadSpeakingRef.current = false
      setCandidateSpeaking(false)
    }
  }

  const startRecord = async () => {
    try {
      // --- Phase 1 (blocking, fast): mic + recorder + UI flip. -----------
      // Goal: get setRecording(true) on screen in well under 1s, even if
      // the candidate is on a slow/restricted network where the streaming
      // WS would take seconds to open. We only await getUserMedia (which
      // is cached on the second turn).
      if (!streamRef.current) {
        streamRef.current = await getUserMediaWithSelectedDevices({ audio: true })
        // Kick off the full-session mixed recording (interviewer voice + every
        // candidate answer) the first time the mic is acquired. Idempotent —
        // wires up whichever of mic/TTS branches aren't connected yet. Never
        // throws into the caller; a failed recording must not block the
        // interview (see sessionRecorder.js).
        startSessionRecording({
          micStream: streamRef.current,
          ttsAudioEl: getTtsAudio(),
          mimeCandidate: pickAudioMimeType(),
        })
      } else {
        // Safety net: if the recorder started before the mic was ready (or the
        // TTS element wasn't created yet on the very first turn), attach now.
        attachMicToSessionRecording(streamRef.current)
        attachTtsToSessionRecording(getTtsAudio())
      }
      setTranscriptLine('')
      finalTranscriptRef.current = ''
      interimTranscriptRef.current = ''
      sttStreamBrokenRef.current = false
      // Reset per-turn proctoring timing/stats + scope the gaze collector to this
      // turn (the "answer tab" just opened — this is the window the user wants
      // measured: long think → fast fluent answer = possible reading).
      speechStatsRef.current = null
      micOpenedAtRef.current = Date.now()
      firstWordAtRef.current = 0
      turnIndexRef.current += 1
      if (collectorStartedRef.current && question?.interactionId) {
        proctoringCollector.setActiveQuestion(turnIndexRef.current, question.interactionId)
      }
      // Reset VAD state for this new turn
      vadSpeakingRef.current = false
      vadEverSpokeRef.current = false
      vadActiveRef.current = false
      setCandidateSpeaking(false)
      if (vadSilenceTimerRef.current) {
        clearTimeout(vadSilenceTimerRef.current)
        vadSilenceTimerRef.current = null
      }
      if (noSpeechTimerRef.current) {
        clearTimeout(noSpeechTimerRef.current)
        noSpeechTimerRef.current = null
      }

      // ALWAYS start MediaRecorder — this is the robust fallback that
      // batch-STT can recover from if the streaming WS never opens.
      try {
        recorderChunksRef.current = []
        // Negotiate a recordable container — iOS Safari only supports audio/mp4
        // and throws on audio/webm, so never hard-code the mimeType.
        const recMime = pickAudioMimeType()
        const rec = new MediaRecorder(
          streamRef.current,
          recMime ? { mimeType: recMime } : undefined
        )
        rec.ondataavailable = (ev) => {
          if (ev.data && ev.data.size > 0) recorderChunksRef.current.push(ev.data)
        }
        rec.start(1000) // emit a chunk every 1s so a teardown captures recent audio
        recorderRef.current = rec
      } catch (e) {
        console.warn('MediaRecorder fallback unavailable:', e?.message || e)
      }

      // Mic is live → flip the UI immediately. Live transcript may show up
      // a moment later once the WS attaches, but the candidate can start
      // talking right now.
      setRecording(true)

      // No-speech watchdog: if VAD is running and the candidate hasn't made a
      // single sound within NO_SPEECH_TIMEOUT_MS, auto-advance to the next
      // question (a silent turn would otherwise leave them stuck). We require
      // vadActiveRef so a degraded turn (worklet never started) never cuts off
      // a real answer it simply couldn't measure — the Done button still works.
      noSpeechTimerRef.current = setTimeout(() => {
        noSpeechTimerRef.current = null
        // Don't auto-advance a "silent" turn while offline — the candidate may
        // be speaking but we just can't reach Deepgram. Wait for reconnection.
        if (isOfflineRef.current) return
        if (recordingRef.current && vadActiveRef.current && !vadEverSpokeRef.current) {
          try { submitTurnFnRef.current() } catch (e) { console.warn('No-speech auto-advance failed:', e?.message || e) }
        }
      }, NO_SPEECH_TIMEOUT_MS)

      // --- Phase 2 (background, slow): streaming WS + AudioWorklet. ------
      // Captured locally so the in-flight setup belongs to this turn and a
      // later stopRecordAndSubmit can cancel it cleanly via wsRef.
      ;(async () => {
        let ws = null
        try {
          ws = await openSttSocket()
        } catch (wsErr) {
          console.warn('[stt-stream] open failed, falling back to batch STT:', wsErr?.message || wsErr)
          return
        }
        // If the user already submitted / ended the turn before the WS
        // came up, drop it.
        if (!recordingRef.current) {
          try { ws.close() } catch (_) {}
          return
        }
        wsRef.current = ws

        try {
          const AC = window.AudioContext || window.webkitAudioContext
          const audioCtx = new AC({ sampleRate: 16000 })
          audioCtxRef.current = audioCtx
          // A suspended AudioContext never runs the worklet's process(), so no
          // PCM is streamed → Deepgram returns nothing → no live interims and
          // the candidate only sees text at "Done" (batch STT). The mic was
          // just opened by a user gesture (the Start/Done button), so resume()
          // is allowed; retry once after a tick if the first attempt is racy.
          if (audioCtx.state === 'suspended') {
            try { await audioCtx.resume() } catch (_) {}
            if (audioCtx.state === 'suspended') {
              await new Promise((r) => setTimeout(r, 50))
              try { await audioCtx.resume() } catch (_) {}
            }
          }
          await installAudioWorklet(audioCtx)
          if (!recordingRef.current) {
            try { ws.close() } catch (_) {}
            try { audioCtx.close() } catch (_) {}
            wsRef.current = null
            audioCtxRef.current = null
            return
          }
          const source = audioCtx.createMediaStreamSource(streamRef.current)
          const node = new AudioWorkletNode(audioCtx, 'pcm-capture-processor')
          node.port.onmessage = (ev) => {
            if (ev.data && typeof ev.data === 'object' && ev.data.type === 'vad') {
              handleVadMessage(ev.data.rms)
              return
            }
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(ev.data)
            }
          }
          source.connect(node)
          const silentGain = audioCtx.createGain()
          silentGain.gain.value = 0
          node.connect(silentGain).connect(audioCtx.destination)
          // If the context gets auto-suspended mid-turn (mobile tab backgrounded,
          // OS audio focus change), process() stops and live captions freeze —
          // resume it so streaming continues.
          audioCtx.onstatechange = () => {
            if (audioCtx.state === 'suspended' && recordingRef.current) {
              audioCtx.resume().catch(() => {})
            }
          }
          sourceNodeRef.current = source
          workletNodeRef.current = node
          silentGainRef.current = silentGain

          ws.addEventListener('close', () => {
            if (wsRef.current === ws) {
              wsRef.current = null
              // MediaRecorder is still running — Done click will fall back to batch.
            }
          })
        } catch (audioErr) {
          console.warn('AudioWorklet setup failed; relying on MediaRecorder fallback:', audioErr?.message || audioErr)
          try { ws.close() } catch (_) {}
          wsRef.current = null
        }
      })()
    } catch (err) {
      console.error('startRecord failed:', err)
      message.error('Could not start microphone')
      try { wsRef.current?.close() } catch (_) {}
      try { audioCtxRef.current?.close() } catch (_) {}
      try { recorderRef.current?.stop() } catch (_) {}
    }
  }

  const tearDownAudioPipeline = async () => {
    try { workletNodeRef.current?.disconnect() } catch (_) {}
    try { sourceNodeRef.current?.disconnect() } catch (_) {}
    try { silentGainRef.current?.disconnect() } catch (_) {}
    try { await audioCtxRef.current?.close() } catch (_) {}
    workletNodeRef.current = null
    sourceNodeRef.current = null
    silentGainRef.current = null
    audioCtxRef.current = null
  }

  const performStopRecordAndSubmit = async ({ endingInterview = false } = {}) => {
    // Use ref instead of state so VAD-triggered calls (whose closures may
    // pre-date the setRecording(true) commit) still see we're recording.
    if (!recordingRef.current) return
    // Snapshot "when did the candidate finish answering" NOW, before any of the
    // WS-close-wait / batch-STT work below runs — that processing time isn't
    // part of how long the candidate took to think + speak.
    const answeredAtMs = Date.now()
    setRecording(false)
    setCandidateSpeaking(false)
    // Clear VAD + no-speech timers so neither fires after we've already submitted
    if (vadSilenceTimerRef.current) {
      clearTimeout(vadSilenceTimerRef.current)
      vadSilenceTimerRef.current = null
    }
    if (noSpeechTimerRef.current) {
      clearTimeout(noSpeechTimerRef.current)
      noSpeechTimerRef.current = null
    }
    setThinking(true)
    setThinkingPhase('next')
    setThinkingText('Finalising transcript…')

    // --- 1) Stop the MediaRecorder so its chunks are flushed ---
    let recorderBlob = null
    try {
      const rec = recorderRef.current
      if (rec && rec.state !== 'inactive') {
        await new Promise((resolve) => {
          rec.onstop = () => resolve()
          try { rec.stop() } catch (_) { resolve() }
        })
        if (recorderChunksRef.current.length > 0) {
          recorderBlob = new Blob(recorderChunksRef.current, {
            type: resolveRecordedMimeType(recorderRef.current, null, null, 'audio/webm'),
          })
        }
      }
    } catch (_) { /* swallow */ }
    recorderRef.current = null
    recorderChunksRef.current = []

    // --- 2) Tell the WS bridge to finish, wait briefly for the 'done' frame ---
    try {
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ cmd: 'finish' }))
        await new Promise((resolve) => {
          let resolved = false
          const onMsg = (ev) => {
            try {
              const m = JSON.parse(ev.data)
              if (m.type === 'done') { resolved = true; resolve() }
            } catch (_) {}
          }
          ws.addEventListener('message', onMsg)
          setTimeout(() => { if (!resolved) resolve() }, 2500)
        })
      }
      try { ws?.close() } catch (_) {}
    } catch (_) { /* swallow */ }
    wsRef.current = null

    await tearDownAudioPipeline()

    // --- 3) Pick the best transcript we have ---
    // Prefer the finalized transcript, but fall back to the live/interim text
    // (what the candidate actually saw on screen) if Deepgram never promoted
    // it to `final` before the done-wait above gave up — otherwise a fully
    // captured answer gets thrown away as "couldn't hear you".
    let answer = (finalTranscriptRef.current || interimTranscriptRef.current || '').trim()

    // Fallback: WS produced nothing (or never opened), OR the backend told us
    // the Deepgram leg died mid-turn (sttStreamBrokenRef) — in both cases the
    // live transcript can't be trusted as complete, so re-transcribe the FULL
    // recording via batch STT and prefer that over a partial/empty live one.
    if ((!answer || sttStreamBrokenRef.current) && recorderBlob && recorderBlob.size > 1000) {
      setThinkingText('Transcribing your answer…')
      try {
        const sttResp = await stt(recorderBlob, sttLanguageCodeRef.current || 'en')
        const batchAnswer = (sttResp?.transcript || '').trim()
        // Batch covers the whole recording, so it's strictly more complete
        // than a live transcript truncated by an upstream drop — but don't
        // overwrite a real partial answer with an empty batch result.
        if (batchAnswer) answer = batchAnswer
      } catch (e) {
        console.warn('Batch STT fallback also failed:', e?.message || e)
      }
    }

    if (!answer) {
      // Candidate stayed silent / nothing transcribable.
      if (!endingInterview && !interviewEndingRequestedRef.current && noAnswerRetryRef.current < 1) {
        // First silent attempt on this question — ask them to try once more on
        // the SAME question instead of skipping straight away. speak() re-opens
        // the mic via autoStartMicIfReady once the prompt finishes.
        noAnswerRetryRef.current += 1
        setThinking(false)
        setThinkingPhase('idle')
        setTranscriptLine('')
        // Keep the SAME question on screen (don't advance the counter).
        message.warning("We couldn't hear you — please answer once more.")
        // This apology is hardcoded English, so it must be SPOKEN as English even
        // in a regional interview — otherwise it is handed to the provider
        // tagged as e.g. Kannada. The provider itself is still chosen from the
        // interview's language set, so the voice does not change.
        speak("Sorry, I couldn't hear you. Could you please answer the question again?", { keepQuestion: question?.text || '', language: 'English' })
        return
      }
      // Already retried once and still silent → record a no-response and let
      // the interview advance. The backend treats this sentinel as a dodge.
      answer = NO_RESPONSE_MARKER
      setTranscriptLine('(No response)')
    } else {
      setTranscriptLine(answer)
    }
    setThinkingPhase('next')

    // --- Pacing nudge: if the candidate runs over the LLM's reference answer-
    // time for 2 consecutive questions, raise a persistent banner that stays up
    // on the following question(s) until they answer one within time. Purely
    // informational — never blocks, skips, or scores anything.
    if (answer && answer !== NO_RESPONSE_MARKER && referenceTimeSecRef.current) {
      const tookSec = (answeredAtMs - micOpenedAtRef.current) / 1000
      if (tookSec > referenceTimeSecRef.current) {
        slowAnswerStreakRef.current += 1
        if (slowAnswerStreakRef.current >= 2) setPacingWarn(true)
      } else {
        slowAnswerStreakRef.current = 0
        setPacingWarn(false)
      }
    }

    // --- Report-only delivery analysis for this turn ---
    // (real answers only — skip the no-response sentinel).
    //
    // NOT gated on collectorStartedRef: these signals come from the SPEECH
    // stats, not the camera. They were previously only computed when the
    // MediaPipe gaze collector had started, so a session where the camera was
    // denied, unavailable, or slow to initialise stored no delivery telemetry
    // at all — which silently emptied the report's pronunciation/fluency block
    // and the read-aloud baseline. Only the calls that actually push events
    // into the gaze collector are still gated, just below.
    let deliverySignals = null
    if (answer && answer !== NO_RESPONSE_MARKER) {
      try {
        const timeToFirstWordMs =
          firstWordAtRef.current && micOpenedAtRef.current
            ? firstWordAtRef.current - micOpenedAtRef.current
            : 0
        // First-word-to-now estimate for when Deepgram's precise per-word
        // timing didn't arrive for this turn (see analyzeAnswerDelivery).
        const fallbackSpokenSec = firstWordAtRef.current ? Math.max(0, (Date.now() - firstWordAtRef.current) / 1000) : 0
        const delivery = analyzeAnswerDelivery(answer, speechStatsRef.current, timeToFirstWordMs, fallbackSpokenSec)
        const meta = {
          interactionId: question.interactionId,
          turnIndex: turnIndexRef.current,
          wpm: delivery.wpm,
          wordCount: delivery.wordCount,
          spokenSec: delivery.spokenSec,
          disfluencyRate: delivery.disfluencyRate,
          timeToFirstWordMs: delivery.timeToFirstWordMs,
        }
        if (collectorStartedRef.current) {
          if (delivery.readingPaceSuspected) {
            proctoringCollector.recordEvent('reading_pace_suspected', {
              severity: delivery.veryFast ? 'high' : 'medium', confidence: 0.7, meta,
            })
          }
          if (delivery.delayedFluentAnswer) {
            proctoringCollector.recordEvent('delayed_fluent_answer', {
              severity: 'high', confidence: 0.75, meta,
            })
          }
          // Feed the read-aloud verdict into the gaze fusion + close the turn so a
          // gaze-down + fast-clean-speech combo corroborate each other.
          proctoringCollector.recordAnswer(question.interactionId, {
            readingSuspected: delivery.readingPaceSuspected || delivery.delayedFluentAnswer,
          })
        }
        // Sent to the backend on EVERY turn (not just suspected ones) so the
        // server can build this candidate's own delivery baseline (avg wpm/
        // disfluency/pacing across their other answers) — the LLM verification
        // layer compares a flagged answer against how THIS candidate normally
        // speaks, not just a generic threshold. The LLM check itself still only
        // runs when this turn's heuristic actually suspects it (gated
        // server-side in aiInterviewHandler.submitTurn).
        deliverySignals = {
          wpm: delivery.wpm,
          wordCount: delivery.wordCount,
          spokenSec: delivery.spokenSec,
          disfluencyRate: delivery.disfluencyRate,
          timeToFirstWordMs: delivery.timeToFirstWordMs,
          pacingCv: delivery.pacingCv,
          avgWordConfidence: delivery.avgWordConfidence,
          // Filler/pause counts and the low-confidence word breakdown also feed
          // the report's fluency + speech-clarity scores (server-side, see
          // student-node helpers/speechQuality). They were previously dropped
          // on this path and only sent by the Deepgram-batch backfill, so an
          // English interview — the one language that never runs that backfill
          // — reached the server with no filler or pause data at all.
          fillerCount: delivery.fillerCount,
          pauseRate: delivery.pauseRate,
          pauseCount: delivery.pauseCount,
          lowConfidenceCount: delivery.lowConfidenceCount,
          lowConfidenceRatio: delivery.lowConfidenceRatio,
          lowConfidenceWords: delivery.lowConfidenceWords,
          readingPaceSuspected: delivery.readingPaceSuspected,
          delayedFluentAnswer: delivery.delayedFluentAnswer,
        }
        if (collectorStartedRef.current && (delivery.readingPaceSuspected || delivery.delayedFluentAnswer)) {
          // The collector normally batches events every 30s — force this one
          // through now so it's already in the DB when submitTurn's LLM
          // verification (below) looks it up by interactionId.
          await proctoringCollector.flush()
        }
      } catch (e) {
        console.warn('[delivery] analysis failed:', e?.message || e)
      }

      // Pronunciation assessment for every language. This also backfills
      // Deepgram delivery signals for Sarvam-routed turns.
      // Fully fire-and-forget: never awaited, never blocks the interview.
      if (recorderBlob && recorderBlob.size > 1000) {
        const interactionIdForTelemetry = question.interactionId
        const timeToFirstWordMsForTelemetry =
          firstWordAtRef.current && micOpenedAtRef.current
            ? firstWordAtRef.current - micOpenedAtRef.current
            : 0
        deliveryTelemetry(recorderBlob, sttLanguageCodeRef.current || 'en')
          .then((rawStats) => {
            if (!rawStats || !rawStats.wordCount) return null
            const enriched = analyzeAnswerDelivery(answer, rawStats, timeToFirstWordMsForTelemetry, rawStats.spokenSec)
            return backfillDeliveryTelemetry(sessionId, interactionIdForTelemetry, {
              wpm: enriched.wpm,
              wordCount: enriched.wordCount,
              spokenSec: enriched.spokenSec,
              disfluencyRate: enriched.disfluencyRate,
              timeToFirstWordMs: enriched.timeToFirstWordMs,
              pacingCv: enriched.pacingCv,
              avgWordConfidence: enriched.avgWordConfidence,
              fillerCount: enriched.fillerCount,
              pauseRate: enriched.pauseRate,
              pauseCount: enriched.pauseCount,
              lowConfidenceCount: enriched.lowConfidenceCount,
              lowConfidenceRatio: enriched.lowConfidenceRatio,
              lowConfidenceWords: enriched.lowConfidenceWords,
              readingPaceSuspected: enriched.readingPaceSuspected,
              delayedFluentAnswer: enriched.delayedFluentAnswer,
              ...(rawStats.pronunciationAssessment
                ? { pronunciationAssessment: rawStats.pronunciationAssessment }
                : {}),
            })
          })
          .catch((e) => console.warn('[proctoring] delivery-telemetry backfill failed:', e?.message || e))
      }
    }

    // Hold the answer so a reconnect can re-submit the SAME turn (the backend
    // keys on interactionId, so a re-send is idempotent) instead of losing it.
    pendingSubmitRef.current = { interactionId: question.interactionId, answer, deliverySignals }
    try {
      const { data } = await withReconnect(
        () => submitTurn(sessionId, question.interactionId, answer, resumeText || null, deliverySignals),
        { onWait: () => { setThinking(true); setThinkingPhase('next'); setThinkingText('Reconnecting…') } }
      )
      pendingSubmitRef.current = null
      // If the user clicked End Interview while submitTurn was in flight,
      // finalize() has already bumped speakSeqRef and set stepRef. Drop
      // the response on the floor so we don't fire a "next question" TTS
      // on top of the ending UI.
      if (stepRef.current !== STEP_LIVE) {
        return
      }
      const dat = data?.data || {}
      if (dat.progress) setProgress(dat.progress)
      if (dat.complete) {
        // Let this submit settle before finalization starts. Awaiting finalize
        // here would deadlock because finalize also waits for the active turn.
        Promise.resolve().then(() => finalizeFnRef.current())
      } else if (endingInterview || interviewEndingRequestedRef.current) {
        // The timer/End button is closing the interview. The response is safely
        // stored; do not render or speak a next question before completion.
        return
      } else {
        const q = dat.question
        // Re-route STT before the next recording starts if the interview just
        // changed language.
        if (dat.languageSwitch) applyLanguageSwitch(dat.languageSwitch)
        setQuestion(q)
        questionLanguageRef.current = q?.questionLanguage || questionLanguageRef.current
        // A reframe is the SAME question reworded (candidate said they didn't
        // understand, or asked to switch language) — keep the counter where it
        // is; only a genuinely new question advances it.
        if (!q?.isReframe) setQuestionNumber((n) => n + 1)
        referenceTimeSecRef.current = q?.referenceTimeSec ?? null
        noAnswerRetryRef.current = 0
        setThinking(false)
        setThinkingPhase('idle')
        speak(q?.text, { speechText: q?.speechText })
      }
    } catch (err) {
      console.error(err)
      // Non-transient (real 4xx) or retries exhausted. Keep the pending answer
      // so the online-listener can retry it when the connection is restored;
      // leave the loader up rather than dead-ending on an error toast.
      if (isTransientErr(err)) {
        setThinking(true)
        setThinkingPhase('next')
        setThinkingText('Waiting for connection…')
        if (endingInterview || interviewEndingRequestedRef.current) throw err
      } else {
        pendingSubmitRef.current = null
        message.error('Failed to submit answer')
        setThinking(false)
        setThinkingPhase('idle')
        if (endingInterview || interviewEndingRequestedRef.current) throw err
      }
    }
  }

  const stopRecordAndSubmit = (options = {}) => {
    if (turnSubmitInFlightRef.current) return turnSubmitInFlightRef.current
    const submission = performStopRecordAndSubmit(options)
      .finally(() => {
        if (turnSubmitInFlightRef.current === submission) {
          turnSubmitInFlightRef.current = null
        }
      })
    turnSubmitInFlightRef.current = submission
    return submission
  }

  // Always route VAD-triggered auto-submit through the latest function so
  // it has fresh closures over sessionId, question, and resumeText.
  useEffect(() => {
    submitTurnFnRef.current = stopRecordAndSubmit
  })

  // Re-send a turn that failed to submit while offline. Mirrors the success
  // handling in stopRecordAndSubmit. Safe to call repeatedly — it no-ops when
  // there's nothing pending or a submit is already in flight.
  const resubmitInFlightRef = useRef(false)
  const resubmitPendingTurn = async () => {
    const pending = pendingSubmitRef.current
    if (!pending || resubmitInFlightRef.current) return
    if (stepRef.current !== STEP_LIVE) return
    resubmitInFlightRef.current = true
    setThinking(true)
    setThinkingPhase('next')
    setThinkingText('Reconnecting…')
    try {
      const { data } = await withReconnect(
        () => submitTurn(sessionId, pending.interactionId, pending.answer, resumeText || null, pending.deliverySignals),
        { onWait: () => { setThinkingText('Waiting for connection…') } }
      )
      pendingSubmitRef.current = null
      if (stepRef.current !== STEP_LIVE) return
      const dat = data?.data || {}
      if (dat.progress) setProgress(dat.progress)
      if (dat.complete) {
        await finalize()
      } else {
        const q = dat.question
        // Re-route STT before the next recording starts if the interview just
        // changed language.
        if (dat.languageSwitch) applyLanguageSwitch(dat.languageSwitch)
        setQuestion(q)
        questionLanguageRef.current = q?.questionLanguage || questionLanguageRef.current
        // A reframe is the SAME question reworded (candidate said they didn't
        // understand, or asked to switch language) — keep the counter where it
        // is; only a genuinely new question advances it.
        if (!q?.isReframe) setQuestionNumber((n) => n + 1)
        referenceTimeSecRef.current = q?.referenceTimeSec ?? null
        noAnswerRetryRef.current = 0
        setThinking(false)
        setThinkingPhase('idle')
        speak(q?.text, { speechText: q?.speechText })
      }
    } catch (err) {
      console.error('resubmit failed:', err)
      if (!isTransientErr(err)) {
        pendingSubmitRef.current = null
        message.error('Failed to submit answer')
        setThinking(false)
        setThinkingPhase('idle')
      }
      // Transient → keep pending; the next 'online' event retries.
    } finally {
      resubmitInFlightRef.current = false
    }
  }
  const resubmitFnRef = useRef(() => {})
  useEffect(() => { resubmitFnRef.current = resubmitPendingTurn })

  // Network awareness: show an overlay while offline and, on reconnect, retry
  // any answer that couldn't be submitted so the interview resumes instead of
  // getting stuck. Listeners are registered once; they dispatch through refs to
  // always hit the latest closures (sessionId, pending answer, etc.).
  useEffect(() => {
    const onOffline = () => setIsOffline(true)
    const onOnline = () => {
      setIsOffline(false)
      // If a turn was mid-submit when the net dropped, push it through now.
      if (pendingSubmitRef.current) {
        try { resubmitFnRef.current() } catch (_) {}
      }
    }
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    return () => {
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
    }
  }, [])

  // `completionReason` says how the interview ended. Omitted by the
  // orchestrator-driven path below, where the server already knows (it decided
  // the interview was over and stamped the session itself).
  const finalize = async (completionReason) => {
    if (finalizeInFlightRef.current) return finalizeInFlightRef.current
    interviewEndingRequestedRef.current = true
    const finalization = (async () => {
      // Preserve the active answer before completing the session. This is the
      // same recorder/STT/submit path as Done answering, including batch-STT
      // fallback and reconnect retries. It also joins a Done click already in
      // flight, so timer + button races remain idempotent.
      if (recordingRef.current || turnSubmitInFlightRef.current) {
        await stopRecordAndSubmit({ endingInterview: true })
      }
      // Claim the recording synchronously so the periodic flush stops issuing
      // partial uploads to the same object key from here on.
      interviewEndedRef.current = true
    // Stop the full-session mixed recording FIRST — before stopCurrentAudio()
    // tears anything down — so it captures right up to the finalize moment.
    // Upload is fire-and-forget: it must never block or fail the candidate's
    // completion flow. Runs in the background while completeSession/fetchReport
    // proceed below.
    stopSessionRecording()
      .then(async (result) => {
        if (!result?.blob) { teardownSessionRecording(); return }
        const assignedId = assessment?.assessment_assigned_id
        if (!assignedId) { teardownSessionRecording(); return }
        // Persist to durable storage (IndexedDB) FIRST so a reload / tab-close
        // / OS-backgrounding right after the "Thank you" screen can't lose the
        // recording — the upload used to be an unawaited in-memory POST that
        // died with the tab. Snapshot the scoped invite JWT too, so a retry
        // after a full tab-close (which clears sessionStorage) can still auth.
        const authToken = readScopedAuthToken()
        // Let any in-flight periodic flush settle first. It carries a SHORTER
        // recording to the same object key, so if it landed after this final
        // upload the stored audio would be silently truncated.
        if (flushInFlightRef.current) {
          await flushInFlightRef.current.catch(() => {})
        }
        await enqueueRecording({
          assessmentAssignedId: assignedId,
          blob: result.blob,
          mimeType: result.mimeType,
          chapters: chaptersRef.current,
          authToken,
        })
        teardownSessionRecording()
        // Kick an immediate attempt; on failure it stays queued and the
        // resume triggers (page load / 'online' / periodic tick) retry it.
        drainPendingUploads()
      })
      .catch((e) => {
        console.warn('[session-recorder] stop failed (non-fatal):', e?.message || e)
        teardownSessionRecording()
      })

    // Hard-kill anything that could still try to play audio for a "next
    // question" — bump the speak() seq so any in-flight tts() request
    // bails out before it instantiates a new Audio element, and stop
    // whatever is currently playing.
    speakSeqRef.current += 1
    stopCurrentAudio()
    clearRevealTimer()
    // Kill the no-speech watchdog so it can't fire a turn after we've wrapped up.
    if (noSpeechTimerRef.current) {
      clearTimeout(noSpeechTimerRef.current)
      noSpeechTimerRef.current = null
    }
    setAiSpeaking(false)
    setDisplayedText('')
    // stepRef is normally synced via useEffect on next render; set it now
    // so autoStartMicIfReady() and stopRecordAndSubmit()'s in-flight
    // submitTurn() see STEP_DONE without waiting for the next tick.
    stepRef.current = STEP_DONE
    setThinking(true)
    setThinkingPhase('finalise')
    setThinkingText('Wrapping up the interview…')
    setStep(STEP_DONE)
    try {
      // Belt-and-suspenders: pass assessmentAssignedId as a fallback so
      // student-node's robust lookup can recover even if sessionId is
      // somehow missing on this client. Retry through reconnects so a drop at
      // the very end doesn't leave the row stuck INPROGRESS.
      await withReconnect(
        () => completeSession(sessionId, 'Candidate', assessment?.assessment_assigned_id, completionReason),
        { onWait: () => { setThinkingText('Waiting for connection…') } }
      )
      // The assignment is now COMPLETED server-side. Persist a local flag so a
      // reload of the standalone /ai-interview/:id route lands on "home" /
      // completion instead of restarting the instructions flow.
      markAiInterviewCompleted(assessment?.assessment_assigned_id)
      const r = await fetchReport(sessionId)
      setReport(r?.data?.data || r?.data)
    } catch (err) {
      console.error(err)
      message.error('Failed to finalise interview')
    } finally {
      setThinking(false)
      setThinkingPhase('idle')
    }
    })()
    finalizeInFlightRef.current = finalization
    try {
      return await finalization
    } finally {
      if (finalizeInFlightRef.current === finalization) {
        finalizeInFlightRef.current = null
      }
    }
  }

  // Same dispatcher pattern as submitTurnFnRef — every render rebinds the
  // ref to the current finalize so the elapsed-timer interval and the
  // violation-modal setTimeout (both captured at mount with sessionId=null)
  // actually invoke a finalize() whose closure sees the real sessionId.
  useEffect(() => {
    finalizeFnRef.current = finalize
  })

  // Leave the completion screen and go "home". For a logged-in student that's
  // the assessment dashboard (where the interview now sits under Completed);
  // setAssessment(null) re-renders the inline list, and navigate('/assessment')
  // covers the standalone /ai-interview/:id route. OTP invite candidates have no
  // dashboard, so we just send them to the app root.
  const goHome = () => {
    try {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    } catch (_) {}
    markAiInterviewCompleted(assessment?.assessment_assigned_id)
    if (isInvite) {
      window.location.assign('/')
      return
    }
    if (typeof setAssessment === 'function') setAssessment(null)
    navigate('/assessment')
  }

  const endInterview = () => setConfirmEnd(true)
  const confirmEndInterview = async () => {
    if (endingNow) return
    setEndingNow(true)
    // Stop AI speech; finalize preserves and submits any answer currently being
    // recorded before it completes the session.
    try { audioRef.current?.pause() } catch (_) {}
    setConfirmEnd(false)
    try {
      await finalize(COMPLETION_REASON.CANDIDATE_INITIATED)
    } finally {
      setEndingNow(false)
    }
  }

  const dimLabel = (id) => ({
    communication: 'Communication',
    role_fit: 'Role Fitment',
    smartness: 'Smartness',
  }[id] || id)

  // ---- Completion screen — exact replica of design's stage-report
  if (step === STEP_BLOCKED) {
    return (
      <Shell className="exam-shell">
        <Body className="exam-scroll" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ maxWidth: 520, padding: 24 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
              Interview already attempted
            </h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569' }}>
              This interview has already been started and can only be taken once,
              so it can’t be retaken. Whatever you answered has been recorded for
              the recruiting team — there’s nothing more to do here.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: '#475569', marginTop: 14 }}>
              You can now close this window.
            </p>
          </div>
        </Body>
      </Shell>
    )
  }

  if (step === STEP_DONE) {
    const sc = report?.score
    const ended = !!report?.interviewIncomplete
    // Greeting name must be the CANDIDATE's name, not the assessment name.
    // getReport returns candidateName/candidateEmail; the 'assessment_invite_name'
    // session key holds the assessment title (used by InviteAssessmentRunner) and
    // must never be used here. Backend falls back candidateName -> "Candidate";
    // drop that so we show the clean no-name greeting instead of "Customer".
    // Also fall back to the live `candidateName` state (populated from
    // startSession / getConfigInfo) so a fresh-tab completion still has the
    // name even if the report fetch happened before the name was known.
    const reportName =
      report?.candidateName && report.candidateName !== 'Candidate'
        ? report.candidateName
        : ''
    const candidateNameRaw =
      reportName ||
      candidateName ||
      report?.candidateEmail ||
      (typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('ai_interview_candidate_email') || ''
        : '') ||
      ''
    const greetingName = (candidateNameRaw.split(/[\s.@]+/)[0] || '').trim()
    const company =
      (typeof sessionStorage !== 'undefined' &&
        (sessionStorage.getItem('assessment_invite_company') ||
          sessionStorage.getItem('ai_interview_company') ||
          '')) || ''

    // Quick summary lines, design uses 4 short factual items with brand-subtle
    // icon tiles. We swap concrete numbers in when we know them, fall back to
    // generic statements otherwise — never invent numbers we don't have.
    const answered = report?.totalAnswered
    const expected = report?.totalExpected
    const durationMins =
      report?.totalDuration ? Math.round((report.totalDuration || 0) / 60) : null
    const summaryLines = [
      {
        text:
          answered != null && expected != null
            ? `${answered} of ${expected} questions answered, audio clear.`
            : answered != null
            ? `${answered} question${answered === 1 ? '' : 's'} answered, audio clear.`
            : 'All questions answered, audio clear.',
        icon: (
          <>
            <path d="M12 3a4 4 0 0 0-4 4v3a4 4 0 0 0 8 0V7a4 4 0 0 0-4-4Z" />
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
          </>
        ),
      },
      {
        text: 'Conversation transcribed and structured for review.',
        icon: (
          <>
            <path d="M8 10h8M8 14h5" />
            <path d="M21 12a9 9 0 1 1-3.5-7.1" />
          </>
        ),
      },
      {
        text: durationMins
          ? `Wrapped in about ${durationMins} minute${durationMins === 1 ? '' : 's'}.`
          : 'Finished comfortably, no rushing.',
        icon: (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </>
        ),
      },
    ]

    return (
      <Shell className="exam-shell">
        <TopBar>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <Brand>AI Interview</Brand>
            {/* Candidate's name (resolved at startSession / getConfigInfo) takes
                priority over the assessment/role name. The DONE screen already
                shows the candidate's first name as a greeting; this keeps the
                TopBar consistent. */}
            <RoleTag>{candidateName || assessment?.roleName || assessment?.title || ''}</RoleTag>
          </div>
          <div style={{ flex: 1 }} />
        </TopBar>
        <Body
          className="exam-scroll"
          style={{ textAlign: 'center', maxWidth: 760, padding: '48px 24px 96px' }}
        >
          {/* .seal — 64px round badge, success or warning */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              background: ended ? '#FFF7E0' : TOKEN.bgSuccessSubtle,
              color: ended ? TOKEN.warning : TOKEN.success,
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 20px',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {ended ? (
                <>
                  <path d="M12 9v4M12 17h.01" />
                  <path d="M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                </>
              ) : (
                <path d="M20 7 9 18l-5-5" />
              )}
            </svg>
          </div>

          {/* h1 — 28px */}
          <h1
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: TOKEN.textPrimary,
              marginBottom: 10,
              letterSpacing: '-0.02em',
            }}
          >
            {ended
              ? greetingName
                ? `Thanks for your time, ${greetingName}.`
                : 'Thanks for your time.'
              : greetingName
              ? `Thanks, ${greetingName}. Your interview is in.`
              : 'Thanks. Your interview is in.'}
          </h1>

          {/* .lede — 16px, muted, max 52ch, centered */}
          <p
            style={{
              fontSize: 16,
              color: TOKEN.textMuted,
              maxWidth: '52ch',
              margin: '0 auto',
              lineHeight: 1.55,
            }}
          >
            {ended
              ? `You ended early, so it's marked incomplete. Your answers so far are saved for the ${company || 'recruiting'} team.`
              : `Sent to the ${company || 'recruiting'} team. A person reviews it before any decision.`}
          </p>

          {/* .report-cols > .summary — Quick summary list */}
          <div style={{ margin: '30px auto 0', maxWidth: 600, textAlign: 'left' }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: TOKEN.textDisabled,
                marginBottom: 14,
              }}
            >
              Quick summary
            </div>
            {summaryLines.map((row, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 13,
                  padding: '13px 0',
                  borderBottom:
                    i < summaryLines.length - 1
                      ? `1px solid ${TOKEN.borderDefault}`
                      : 'none',
                  fontSize: 14.5,
                  color: TOKEN.textBody,
                  lineHeight: 1.55,
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: TOKEN.radiusMd,
                    background: TOKEN.bgBrandSubtle,
                    color: TOKEN.primary600,
                    display: 'grid',
                    placeItems: 'center',
                    flex: 'none',
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {row.icon}
                  </svg>
                </span>
                <span>{row.text}</span>
              </div>
            ))}
          </div>

          {/* .foot — final line, then a single primary action */}
          <p
            style={{
              marginTop: 26,
              fontSize: 13,
              color: TOKEN.textMuted,
              lineHeight: 1.55,
            }}
          >
            No score here, that goes only to the recruiting team.
          </p>

          {isInvite ? (
            <p
              style={{
                marginTop: 18,
                fontSize: 13,
                color: TOKEN.textMuted,
                lineHeight: 1.55,
              }}
            >
              You can now close this window.
            </p>
          ) : (
            <div style={{ marginTop: 28 }}>
              <PrimaryBtn onClick={goHome}>Back to Assessments</PrimaryBtn>
            </div>
          )}
        </Body>
      </Shell>
    )
  }

  // ---- Live screen
  return (
    <Shell className="exam-shell">
      {isOffline && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: '#b91c1c', color: '#fff', textAlign: 'center',
            padding: '10px 16px', fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: '#fff',
            display: 'inline-block', opacity: 0.85,
          }} />
          You’re offline — waiting to reconnect. Your interview is paused; don’t close this tab.
        </div>
      )}
      {showReentryModal && (
        <ViolationOverlay>
          <ViolationCard>
            <ViolationTitle>Return to Assessment Mode</ViolationTitle>
            <ViolationBody>
              You have exited fullscreen / switched away from the interview. For security
              and proctoring purposes, you must return to full-page mode to continue.
            </ViolationBody>
            <WarningBox>
              <strong>Warning {fsViolations} / 3.</strong> A fourth violation will result in automatic submission of your interview and will be flagged as suspicious.
            </WarningBox>
            <div style={{ textAlign: 'center' }}>
              <PrimaryBtn onClick={reenterFullscreen}>Return to Interview</PrimaryBtn>
            </div>
          </ViolationCard>
        </ViolationOverlay>
      )}

      {showViolationModal && (
        <ViolationOverlay>
          <ViolationCard>
            <ViolationTitle>Interview will be auto-submitted</ViolationTitle>
            <ViolationBody>
              You&apos;ve violated the rules more than 3 times, so your assessment will be auto-submitted.
            </ViolationBody>
            <div style={{ textAlign: 'center' }}>
              <PrimaryBtn onClick={() => finalizeFnRef.current(COMPLETION_REASON.PROCTORING_VIOLATION)}>Understood</PrimaryBtn>
            </div>
          </ViolationCard>
        </ViolationOverlay>
      )}

      {confirmEnd && (
        <ViolationOverlay onClick={() => !endingNow && setConfirmEnd(false)}>
          <ViolationCard onClick={(e) => e.stopPropagation()}>
            <ViolationTitle>End the interview?</ViolationTitle>
            <ViolationBody>
              The AI will stop asking questions and score your interview based on what
              you&apos;ve answered so far. You can&apos;t restart this interview once it&apos;s ended.
            </ViolationBody>
            <WarningBox>
              Only end early if you&apos;re sure — your recruiter will see this was a
              candidate-initiated wrap-up.
            </WarningBox>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <GhostBtn
                onClick={() => setConfirmEnd(false)}
                disabled={endingNow}
              >
                Continue interviewing
              </GhostBtn>
              <DangerBtn
                onClick={confirmEndInterview}
                loading={endingNow}
                disabled={endingNow}
              >
                {endingNow ? 'Ending…' : 'Yes, end it'}
              </DangerBtn>
            </div>
          </ViolationCard>
        </ViolationOverlay>
      )}

      <TopBar>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <Brand>AI Interview</Brand>
          {/* Candidate's name (from startSession/getConfigInfo) takes priority
              over the assessment/role name — a prior bug had this showing the
              role name (e.g. "Customer") as if it were the candidate. */}
          <RoleTag>{candidateName || assessment?.roleName || assessment?.title || ''}</RoleTag>
        </div>
        <div style={{ flex: 1 }} />
        {(() => {
          // progress starts as DEFAULT_PROGRESS (placeholder sections, not the
          // real config) until the first API response replaces it. Rendering a
          // total off that placeholder briefly flashed a wrong number (e.g.
          // "1 of 6") before the real total_expected arrived — so show nothing
          // until progress has actually loaded, rather than guess.
          if (progress === DEFAULT_PROGRESS) return null
          // Total comes from the backend's admin-configured budget
          // (progress.totalExpected = max_questions, incl. intro). Falls back to
          // the per-parameter section sum if that's ever missing. Current is
          // clamped to total.
          const sectionsTotal = Array.isArray(progress?.sections)
            ? progress.sections.reduce((sum, s) => sum + (Number(s?.total) || 0), 0)
            : 0
          const totalQuestions = Number(progress?.totalExpected) || sectionsTotal
          if (!totalQuestions) return null
          const current = Math.min(questionNumber, totalQuestions)
          return (
            <ElapsedPill style={{ marginRight: 8 }} aria-label={`Question ${current} of ${totalQuestions}`}>
              Question {current} of {totalQuestions}
            </ElapsedPill>
          )
        })()}
        <ElapsedPill
          style={
            remaining <= 60
              ? { color: TOKEN.error, boxShadow: `inset 0 0 0 1px ${TOKEN.error}` }
              : remaining <= 300
              ? { color: TOKEN.warning, boxShadow: `inset 0 0 0 1px ${TOKEN.warning}` }
              : undefined
          }
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {fmtTime(remaining)}
        </ElapsedPill>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu((v) => !v)}
            style={{
              background: TOKEN.bgElevated,
              border: 'none',
              cursor: 'pointer',
              width: 38,
              height: 38,
              borderRadius: TOKEN.radiusMd,
              color: TOKEN.textMuted,
              boxShadow: `inset 0 0 0 1px ${TOKEN.borderDefault}`,
              display: 'grid',
              placeItems: 'center',
            }}
            aria-label="More actions"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.7" />
              <circle cx="12" cy="12" r="1.7" />
              <circle cx="12" cy="19" r="1.7" />
            </svg>
          </button>
          {showMenu && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 11 }} onClick={() => setShowMenu(false)} />
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  marginTop: 8,
                  background: TOKEN.bgElevated,
                  border: `1px solid ${TOKEN.borderDefault}`,
                  borderRadius: TOKEN.radiusMd,
                  boxShadow: TOKEN.shadowLg,
                  zIndex: 12,
                  minWidth: 230,
                  padding: 6,
                }}
              >
                <button
                  onClick={() => { setShowMenu(false); endInterview() }}
                  disabled={!sessionId}
                  style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    gap: 11,
                    height: 40,
                    padding: '0 12px',
                    border: 'none',
                    background: 'none',
                    cursor: !sessionId ? 'not-allowed' : 'pointer',
                    fontSize: 13.5,
                    color: TOKEN.error,
                    fontWeight: 500,
                    borderRadius: TOKEN.radiusSm,
                    fontFamily: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" rx="1" />
                  </svg>
                  End interview
                </button>
              </div>
            </>
          )}
        </div>
      </TopBar>

      <Body className="exam-scroll" style={{ textAlign: 'center' }}>
        {/* --- Abstract AI avatar (idle / speaking / listening / thinking) --- */}
        <Avatar>
          <AvatarRingsBox speaking={aiSpeaking || (thinking && thinkingPhase === 'next')} listening={recording}>
            <AvatarRing />
            <AvatarRing />
            <AvatarRing />
          </AvatarRingsBox>
          <AvatarCore speaking={aiSpeaking} listening={recording} />
          <AvatarStateLabel>
            {aiSpeaking ? 'Speaking' : recording ? 'Listening' : thinking ? 'Thinking' : 'Idle'}
          </AvatarStateLabel>
        </Avatar>

        {/* Pacing banner — shown once the candidate has run over the reference
            answer-time for 2 consecutive questions, until they answer in time. */}
        {pacingWarn && (
          <div
            role="status"
            style={{
              margin: '0 auto 14px',
              maxWidth: 560,
              background: '#FFF7ED',
              border: '1px solid #FED7AA',
              color: '#9A3412',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            You need to speed up — we have more questions to go.
          </div>
        )}

        {/* --- Question text OR shimmer-loader while AI evaluates / prepares --- */}
        {/* Show the active loader the whole time we're between turns ('next') or
            wrapping up ('finalise') — not just when there's no question text.
            Otherwise the stale previous question sits on screen after "Done
            answering" and the wait reads as a freeze. */}
        {thinking && (thinkingPhase === 'next' || thinkingPhase === 'finalise') ? (
          <QLoader>
            <QlShim>
              <i />
              <i />
              <i />
            </QlShim>
            <QlMsg>
              <span className="dot" />
              {thinkingText || (thinkingPhase === 'finalise' ? 'Evaluating your responses…' : 'Cooking up the next question…')}
            </QlMsg>
          </QLoader>
        ) : (
          <QuestionText>
            {displayedText ? (
              <>
                {displayedText}
                {aiSpeaking && displayedText.length < (question?.text?.length || 0) && (
                  <SubtitleCaret />
                )}
              </>
            ) : aiSpeaking ? (
              <span style={{ color: TOKEN.textDisabled }}>
                <SubtitleCaret />
              </span>
            ) : question?.text ? (
              <>{question.text}</>
            ) : (
              <span style={{ color: TOKEN.textDisabled }}>Preparing your first question…</span>
            )}
          </QuestionText>
        )}

        {/* --- Recording status. The candidate never sees the transcript: it is
             captured for scoring only (as in the v2 candidate app). --- */}
        {recording && (
          <TranscriptBox>
            <div className="tl">
              <span className="live">
                <span className="d" />
                Recording your answer
              </span>
            </div>
            <div className="body">
              <span className="ph">Speak your answer, then select Done answering.</span>
            </div>
          </TranscriptBox>
        )}

        {/* --- Controls / status row --- */}
        <IvControls className="exam-actions">
          {recording && (
            <div className="row">
              <DangerBtn onClick={stopRecordAndSubmit}>Done answering</DangerBtn>
            </div>
          )}
          {thinking && thinkingPhase !== 'next' && thinkingPhase !== 'finalise' && (
            <HintRow>
              <Spin size="small" />
              <span>{thinkingText}</span>
            </HintRow>
          )}
          {!recording && !thinking && aiSpeaking && (
            <HintRow>
              <span>The AI is asking — your mic will open automatically.</span>
            </HintRow>
          )}
          {!recording && !thinking && !aiSpeaking && !transcriptLine && (
            <HintRow>
              <Spin size="small" />
              <span>Opening your microphone…</span>
            </HintRow>
          )}
        </IvControls>
      </Body>

      {/* --- Self-view camera PiP (fixed bottom-right) --- */}
      <SelfCameraPip>
        <SelfVideo ref={selfVideoRef} autoPlay muted playsInline />
        {!cameraReady && (
          <PipPlaceholder>
            <Spin size="small" />
            <span>Starting camera…</span>
          </PipPlaceholder>
        )}
        {recording && (
          <PipRecBadge>
            <span className="d" />
            REC
          </PipRecBadge>
        )}
        <PipLabel>You</PipLabel>
      </SelfCameraPip>
    </Shell>
  )
}

export default Interview
